import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Card,
  Row,
  Col,
  Typography,
  message,
  Steps,
  Divider,
  Space,
  Modal,
  InputNumber,
  Tooltip,
  Radio
} from 'antd';
import { Tag } from 'antd';
import { DataTable } from '../../../ui';
import { 
  SaveOutlined, 
  ArrowLeftOutlined, 
  PlusOutlined, 
  DeleteOutlined,
  EditOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useCreateApplicationMutation, useUpdateApplicationMutation, useGetApplicationByIdQuery } from '../../../store/api/applicationsApi';
import { useGetContractorsByRoleQuery, ContractorRole, useGetContractsByClientIdQuery } from '../../../store/api/referencesApi';
import { useUpdateCargoMutation, CargoImportRow } from '../../../store/api/cargoApi';
import { useGetUsersQuery } from '../../../store/api/usersApi';
import { Application, ApplicationCondition, ApplicationConditionType, CreateApplicationRequest } from '../../../types';
import CreateContractorModal from '../../common/CreateContractorModal';
import AddressAutocomplete from '../../common/AddressAutocomplete';
import type { AddressSuggestion } from '../../../services/addressService';
import { normalizeCityLabel, resolveCityHintFromAddressString } from '../../../utils/addressUtils';
import {
  APPLICATION_CONDITION_LABELS,
  APPLICATION_CONDITION_OPTIONS,
  formatApplicationCondition,
} from '../../../utils/applicationConditions';
import SmartSelect from '../../common/SmartSelect';
import CargoImportModal from '../../common/CargoImportModal';
import type { Contractor, ContractRow } from '../../../store/api/referencesApi';
import { API_BASE_URL } from '../../../constants';
import {
  formatResponsibleUserLabel,
  mergeResponsibleSelectUsers,
} from '../../../utils/userDisplayName';
import dayjs from 'dayjs';

async function resolveCityForAddressSelection(
  value: string,
  suggestion?: AddressSuggestion
): Promise<string | undefined> {
  const fromGeo = normalizeCityLabel(suggestion?.city);
  if (fromGeo) return fromGeo;
  const q = value.trim();
  if (q.length < 3) return undefined;
  const hint = await resolveCityHintFromAddressString(q);
  return hint ?? undefined;
}

/**
 * При возврате с шага «Грузы» на шаг 1: в step1Data даты могли оказаться строками (например basisDate).
 * DatePicker Ant Design ожидает dayjs — иначе внутри срабатывает date.isValid is not a function.
 */
function coerceDateFieldToDayjs(v: unknown): dayjs.Dayjs | undefined {
  if (v == null || v === '') return undefined;
  if (typeof v === 'object' && v !== null && typeof (v as { isValid?: () => boolean }).isValid === 'function') {
    const d = v as dayjs.Dayjs;
    return d.isValid?.() ? d : undefined;
  }
  if (v instanceof Date && !isNaN(v.getTime())) {
    const d = dayjs(v);
    return d.isValid() ? d : undefined;
  }
  if (typeof v === 'string') {
    const d = dayjs(v);
    return d.isValid() ? d : undefined;
  }
  return undefined;
}

function normalizeStep1FormValuesForRestore(data: Record<string, unknown>): Record<string, unknown> {
  if (!data || typeof data !== 'object') return data;
  const keys = ['applicationDate', 'plannedLoadingDate', 'plannedUnloadingDate', 'basisDate'] as const;
  const out: Record<string, unknown> = { ...data };
  for (const k of keys) {
    if (!(k in out) || out[k] == null || out[k] === '') continue;
    const coerced = coerceDateFieldToDayjs(out[k]);
    if (coerced !== undefined) out[k] = coerced;
  }
  return out;
}

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;

interface CargoItem {
  key: string;
  id?: number; // ID груза в БД (если есть)
  name: string;
  description: string;
  quantity: number;
  weightPerItem: number;
  lengthM: number | null | undefined;
  widthM: number | null | undefined;
  heightM: number | null | undefined;
  totalWeight: number;
  totalVolume: number | null | undefined;
  packagingType: string;
  cargoCharacter: string;
  isLocal?: boolean;
}

interface ApplicationFormProps {
  visible?: boolean;
  application?: Application;
  applicationId?: number;
  onCancel: () => void;
  onSuccess: (application: Application) => void;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({
  visible = false,
  application: propApplication,
  applicationId,
  onCancel,
  onSuccess
}) => {
  // Функция для безопасного парсинга Decimal значений из Prisma
  const parseDecimal = (value: any): number | null => {
    console.log('🔍 parseDecimal input:', { value, type: typeof value, isObject: typeof value === 'object' });
    
    if (value === null || value === undefined) {
      console.log('  → null/undefined, returning null');
      return null;
    }
    
    // Если это объект Prisma Decimal
    if (typeof value === 'object' && value !== null) {
      console.log('  → Object detected, keys:', Object.keys(value), 'has toNumber:', 'toNumber' in value);
      
      // Проверяем различные варианты Prisma Decimal
      if ('toNumber' in value && typeof value.toNumber === 'function') {
        const result = value.toNumber();
        console.log('  → Prisma Decimal, toNumber() result:', result);
        return result;
      }
      
      // Если есть свойство value
      if ('value' in value && typeof value.value === 'string') {
        const parsed = parseFloat(value.value);
        const result = isNaN(parsed) ? null : parsed;
        console.log('  → Decimal with value property, parsed:', result);
        return result;
      }
      
      // Если это объект с числовыми свойствами
      if ('d' in value && Array.isArray(value.d)) {
        console.log('  → Decimal array format, d:', value.d);
        // Это может быть другой формат Decimal
        return null;
      }
      
      console.log('  → Unknown object format, returning null');
      return null;
    }
    
    // Если это строка
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      const result = isNaN(parsed) ? null : parsed;
      console.log('  → string, parseFloat result:', result);
      return result;
    }
    
    // Если это число
    if (typeof value === 'number') {
      const result = isNaN(value) ? null : value;
      console.log('  → number, result:', result);
      return result;
    }
    
    console.log('  → unknown type, returning null');
    return null;
  };

  // Функция для парсинга обязательных числовых полей (возвращает 0 вместо null)
  const parseRequiredDecimal = (value: any): number => {
    const result = parseDecimal(value);
    return result !== null ? result : 0;
  };
  // Проверка состояния авторизации
  React.useEffect(() => {
    const token = localStorage.getItem('logistics_access_token');
    const refreshToken = localStorage.getItem('logistics_refresh_token');
    const user = localStorage.getItem('logistics_user');
    
    console.log('🔐 Auth state check:', {
      hasToken: !!token,
      hasRefreshToken: !!refreshToken,
      hasUser: !!user,
      token: token ? token.substring(0, 20) + '...' : 'none'
    });
  }, []);

  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cargoList, setCargoList] = useState<CargoItem[]>([]);
  const [cargoModalVisible, setCargoModalVisible] = useState(false);
  const [editingCargo, setEditingCargo] = useState<CargoItem | null>(null);
  const [cargoForm] = Form.useForm();
  const [conditions, setConditions] = useState<ApplicationCondition[]>([]);
  const [conditionModalVisible, setConditionModalVisible] = useState(false);
  const [editingConditionIndex, setEditingConditionIndex] = useState<number | null>(null);
  const [conditionForm] = Form.useForm();
  const selectedConditionType = Form.useWatch('type', conditionForm) as ApplicationConditionType | undefined;
  const [cargoImportModalVisible, setCargoImportModalVisible] = useState(false);
  const [step1Data, setStep1Data] = useState<any>({});
  const [pendingNames, setPendingNames] = useState<{
    clientName?: string;
    expeditorName?: string;
    shipperName?: string;
    consigneeName?: string;
  } | null>(null);
  
  // Дополнительное состояние для критически важных полей (защита от потери данных)
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedExpeditorId, setSelectedExpeditorId] = useState<number | null>(null);
  const [selectedShipperId, setSelectedShipperId] = useState<number | null>(null);
  const [selectedConsigneeId, setSelectedConsigneeId] = useState<number | null>(null);

  // Состояние для единого модального окна создания контрагента
  const [contractorModalVisible, setContractorModalVisible] = useState(false);
  const [contractorDefaultRoles, setContractorDefaultRoles] = useState<ContractorRole[]>([]);

  // Черновик формы (автосохранение)
  const APPLICATION_DRAFT_KEY = 'draft_application_form_v2';
  const saveDraft = (extra: any = {}) => {
    try {
      const values = form.getFieldsValue();
      // Безопасная обработка дат для сохранения в черновик
      const safeDateValue = (dateValue: any) => {
        if (!dateValue) return null;
        // Если это dayjs объект
        if (dateValue && typeof dateValue.format === 'function') {
          return dateValue.format('YYYY-MM-DD');
        }
        // Если это строка
        if (typeof dateValue === 'string') {
          return dateValue;
        }
        // Если это Date объект
        if (dateValue instanceof Date) {
          return dateValue.toISOString().slice(0, 10);
        }
        return null;
      };
      
      const draft = {
        step: currentStep,
        values: {
          ...values,
          basisDate: safeDateValue(values.basisDate),
          plannedLoadingDate: safeDateValue(values.plannedLoadingDate),
          plannedUnloadingDate: safeDateValue(values.plannedUnloadingDate),
          applicationDate: safeDateValue(values.applicationDate)
        },
        cargoList,
        ...extra
      };
      localStorage.setItem(APPLICATION_DRAFT_KEY, JSON.stringify(draft));
    } catch (e) {
      // ignore draft errors
    }
  };
  const clearDraft = () => {
    localStorage.removeItem(APPLICATION_DRAFT_KEY);
  };

  // Загружаем данные заявки если передан applicationId
  const { 
    data: fetchedApplication, 
    isLoading: isLoadingApplication,
    error: loadingError,
    isError: hasLoadingError
  } = useGetApplicationByIdQuery(
    applicationId!,
    { 
      skip: !visible || !applicationId,
      refetchOnMountOrArgChange: true
    }
  );

  console.log('🔍 useGetApplicationByIdQuery state:', {
    applicationId,
    isLoading: isLoadingApplication,
    hasData: !!fetchedApplication,
    hasError: hasLoadingError,
    error: loadingError
  });

  // Временная функция для тестирования API
  const testApiCall = async () => {
    console.log('🧪 Testing direct API call...');
    try {
      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('logistics_access_token') || 'no-token'}`
        }
      });
      console.log('🧪 Direct API response status:', response.status);
      const data = await response.json();
      console.log('🧪 Direct API response data:', data);
    } catch (error) {
      console.error('🧪 Direct API call failed:', error);
    }
  };

  // Используем либо пропс application, либо загруженное из API
  const application = propApplication || fetchedApplication;
  const isEdit = !!application;

  const [createApplication] = useCreateApplicationMutation();
  const [updateApplication] = useUpdateApplicationMutation();
  const [updateCargo] = useUpdateCargoMutation();

  // Загрузка справочников из API
  const { data: clients = [], isLoading: isLoadingClients, error: clientsError } = useGetContractorsByRoleQuery('client');
  const { data: expeditors = [], isLoading: isLoadingExpeditors, error: expeditorsError } = useGetContractorsByRoleQuery('expeditor');
  const { data: shippers = [], isLoading: isLoadingShippers, error: shippersError } = useGetContractorsByRoleQuery('shipper');
  const { data: consignees = [], isLoading: isLoadingConsignees, error: consigneesError } = useGetContractorsByRoleQuery('consignee');
  const { data: users = [], isLoading: isLoadingUsers } = useGetUsersQuery();

  const watchedClientId = Form.useWatch('clientId', form);
  const watchedApplicationBasis = Form.useWatch('applicationBasis', form);
  const clientIdForContracts =
    watchedClientId != null && watchedClientId !== '' && !Number.isNaN(Number(watchedClientId))
      ? Number(watchedClientId)
      : undefined;
  const basisForContractUi = watchedApplicationBasis ?? 'CLIENT_CONTRACT';
  const { data: contractsByClient = [], isFetching: contractsLoading } = useGetContractsByClientIdQuery(
    clientIdForContracts ?? 0,
    { skip: !clientIdForContracts || basisForContractUi !== 'CLIENT_CONTRACT' }
  );

  const applyFinancialFieldsFromContract = useCallback(
    (c: ContractRow) => {
      const patch: Record<string, number> = {};
      if (c.prepaymentPercent != null) patch.prepaymentPercent = c.prepaymentPercent;
      if (c.prepaymentDelayDays != null) patch.prepaymentDelayDays = c.prepaymentDelayDays;
      if (c.postpaymentPercent != null) patch.mainPaymentPercent = c.postpaymentPercent;
      if (c.postpaymentDelayDays != null) patch.mainPaymentDelayDays = c.postpaymentDelayDays;
      if (Object.keys(patch).length > 0) {
        form.setFieldsValue(patch);
      }
    },
    [form]
  );

  useEffect(() => {
    if (watchedApplicationBasis === 'CLIENT_CONTRACT_REQUEST') {
      form.setFieldValue('contractId', undefined);
    }
  }, [watchedApplicationBasis, form]);

  /** Один договор — выставить contractId и подстановку; ноль — сбросить contractId. */
  useEffect(() => {
    if (basisForContractUi !== 'CLIENT_CONTRACT' || !clientIdForContracts || contractsLoading) {
      return;
    }
    const list = contractsByClient;
    if (list.length === 0) {
      form.setFieldValue('contractId', undefined);
      return;
    }
    if (list.length !== 1) {
      return;
    }
    const only = list[0];
    if (only.clientId !== clientIdForContracts) {
      return;
    }
    const current = form.getFieldValue('contractId');
    if (current === only.id) {
      return;
    }
    form.setFieldValue('contractId', only.id);
    applyFinancialFieldsFromContract(only);
  }, [
    basisForContractUi,
    clientIdForContracts,
    contractsLoading,
    contractsByClient,
    form,
    applyFinancialFieldsFromContract
  ]);

  // Получаем текущего пользователя и проверяем роль
  const currentUser = React.useMemo(() => {
    try {
      const userStr = localStorage.getItem('logistics_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }, []);

  const canEditResponsibleLogist = React.useMemo(() => {
    if (!currentUser?.role) return false;
    const roleName = typeof currentUser.role === 'string' ? currentUser.role : currentUser.role?.name;
    return ['admin', 'accountant', 'manager'].includes(roleName?.toLowerCase() || '');
  }, [currentUser]);

  // Общее состояние загрузки справочников
  const isLoadingReferences = isLoadingClients || isLoadingExpeditors || isLoadingShippers || isLoadingConsignees;
  const hasReferenceErrors = clientsError || expeditorsError || shippersError || consigneesError;



  // Сброс формы только при первом открытии (не затирать данные после импорта)
  useEffect(() => {
    if (visible && !application && !applicationId) {
      // Если уже что-то введено/подставлено — не сбрасываем
      const cur = form.getFieldsValue();
      const hasAny = !!(
        cur.externalNumber || cur.applicationDate || cur.loadingAddress || cur.unloadingAddress ||
        cur.plannedLoadingDate || cur.plannedUnloadingDate || cur.requiredConditions || cur.totalCostWithVAT
      );
      if (hasAny) {
        console.log('⏭️ Skip reset: form already has values');
        return;
      }

      console.log('🔄 Resetting form for new application creation (first open)');
      // Попробуем восстановить черновик
      const rawDraft = localStorage.getItem(APPLICATION_DRAFT_KEY);
      if (rawDraft) {
        try {
          const draft = JSON.parse(rawDraft);
          const values = draft.values || {};
          
          // Безопасная конвертация дат из черновика
          const safeDayjs = (dateValue: any) => {
            if (!dateValue) return undefined;
            try {
              const dayjsValue = dayjs(dateValue);
              if (dayjsValue && typeof dayjsValue.isValid === 'function' && dayjsValue.isValid()) {
                return dayjsValue;
              }
            } catch (e) {
              console.warn('Ошибка при конвертации даты из черновика:', e);
            }
            return undefined;
          };
          
          form.setFieldsValue({
            ...values,
            applicationDate: safeDayjs(values.applicationDate),
            plannedLoadingDate: safeDayjs(values.plannedLoadingDate),
            plannedUnloadingDate: safeDayjs(values.plannedUnloadingDate),
            basisDate: safeDayjs(values.basisDate),
          });
          setCargoList(Array.isArray(draft.cargoList) ? draft.cargoList : []);
          setCurrentStep(Number.isInteger(draft.step) ? draft.step : 0);
        } catch {
          form.resetFields();
          setCurrentStep(0);
          setCargoList([]);
        }
      } else {
        form.resetFields();
        setCurrentStep(0);
        setCargoList([]);
        setConditions([]);
      }
      setStep1Data({});
      setSelectedClientId(null);
      setLoading(false);
    }
  }, [visible, application, applicationId, form]);

  useEffect(() => {
    console.log('🔄 ApplicationForm useEffect triggered');
    console.log('  applicationId:', applicationId);
    console.log('  propApplication:', propApplication);
    console.log('  fetchedApplication:', fetchedApplication);
    console.log('  isLoadingApplication:', isLoadingApplication);
    console.log('  isLoadingReferences:', isLoadingReferences);
    
    // Ждем загрузки и справочников и заявки (если нужно)
    if (isLoadingApplication || isLoadingReferences) {
      console.log('⏳ Still loading data...');
      return;
    }
    
    if (hasReferenceErrors) {
      console.error('❌ Error loading references:', {
        clientsError,
        expeditorsError,
        shippersError,
        consigneesError
      });
      message.error('Ошибка загрузки справочников');
      return;
    }
    
    // ✅ ИСПРАВЛЕНИЕ: Приоритет отдаем fetchedApplication (данным от API), а не propApplication
    const application = fetchedApplication || propApplication;
    console.log('  final application:', application);
    console.log('  source:', fetchedApplication ? 'fetchedApplication (API)' : 'propApplication (props)');
    
    // КРИТИЧЕСКАЯ ПРОВЕРКА: Не переустанавливаем форму если пользователь уже что-то ввел
    // Это предотвращает потерю данных при загрузке справочников
    const currentFormValues = form.getFieldsValue();
    const hasUserData = currentFormValues.externalNumber || 
                       currentFormValues.clientId || 
                       currentFormValues.expeditorId ||
                       currentFormValues.shipperId ||
                       currentFormValues.consigneeId ||
                       currentFormValues.applicationDate ||
                       currentFormValues.loadingAddress ||
                       currentFormValues.unloadingAddress;
    
    // ВАЖНО: если есть данные заявки, всегда обновляем форму из API/props
    // Ограничиваем предотвращение сброса только когда заявки нет вовсе
    if (hasUserData && !application) {
      console.log('⚠️ User has already entered data, skipping form reset to prevent data loss');
      console.log('🔍 Current user data:', {
        externalNumber: currentFormValues.externalNumber,
        clientId: currentFormValues.clientId,
        expeditorId: currentFormValues.expeditorId,
        shipperId: currentFormValues.shipperId,
        consigneeId: currentFormValues.consigneeId,
        applicationDate: currentFormValues.applicationDate,
        loadingAddress: currentFormValues.loadingAddress,
        unloadingAddress: currentFormValues.unloadingAddress
      });
      return;
    }
    
    if (application) {
      console.log('📝 Setting form values for editing:', {
        id: application.id,
        externalNumber: application.externalNumber,
        clientId: application.clientId,
        expeditorId: application.expeditorId,
        shipperId: application.shipperId,
        consigneeId: application.consigneeId,
        cargoCount: application.cargo?.length || 0
      });

      console.log('🔍 Full application object:', application);
      console.log('🔍 Application structure:', {
        hasClient: !!application.client,
        hasExpeditor: !!application.expeditor,
        hasShipper: !!application.shipper,
        hasConsignee: !!application.consignee,
        hasCargo: !!application.cargo,
        cargoLength: application.cargo?.length
      });
      
      // 🔍 🔍 🔍 ДОПОЛНИТЕЛЬНАЯ ДИАГНОСТИКА: Что получает компонент от RTK Query
      if (application.cargo && Array.isArray(application.cargo)) {
        console.log('🔍 🔍 🔍 ДАННЫЕ ГРУЗА В КОМПОНЕНТЕ (В useEffect):');
        application.cargo.forEach((cargo, index) => {
          console.log(`  Груз ${index} (${cargo.name}) в useEffect:`, {
            id: cargo.id,
            lengthM: { value: cargo.lengthM, type: typeof cargo.lengthM, isNull: cargo.lengthM === null, isUndefined: cargo.lengthM === undefined },
            widthM: { value: cargo.widthM, type: typeof cargo.widthM, isNull: cargo.widthM === null, isUndefined: cargo.widthM === undefined },
            heightM: { value: cargo.heightM, type: typeof cargo.heightM, isNull: cargo.heightM === null, isUndefined: cargo.heightM === undefined },
            volumeM3: { value: cargo.volumeM3, type: typeof cargo.volumeM3, isNull: cargo.volumeM3 === null, isUndefined: cargo.volumeM3 === undefined }
          });
        });
      }

      // 🔍 ДОПОЛНИТЕЛЬНАЯ ДИАГНОСТИКА ДЛЯ РЕДАКТИРОВАНИЯ
      console.log('🔍 ДИАГНОСТИКА КОНТРАГЕНТОВ ПРИ РЕДАКТИРОВАНИИ:', {
        'Данные из API': {
          clientId: { value: application.clientId, type: typeof application.clientId },
          clientName: application.client?.name,
          expeditorId: { value: application.expeditorId, type: typeof application.expeditorId },
          expeditorName: application.expeditor?.name,
          shipperId: { value: application.shipperId, type: typeof application.shipperId },
          shipperName: application.shipper?.name,
          consigneeId: { value: application.consigneeId, type: typeof application.consigneeId },
          consigneeName: application.consignee?.name
        },
        'Проверка равенства': {
          'expeditorId === clientId': application.expeditorId === application.clientId,
          'shipperId === clientId': application.shipperId === application.clientId,
          'consigneeId === clientId': application.consigneeId === application.clientId,
          'expeditorId === shipperId': application.expeditorId === application.shipperId,
          'expeditorId === consigneeId': application.expeditorId === application.consigneeId,
          'shipperId === consigneeId': application.shipperId === application.consigneeId
        }
      });

      const formValues = {
        // Для редактирования: basisNumber и basisDate берём из application
        // Если это партнёрская заявка, то externalNumber -> basisNumber, applicationDate -> basisDate
        basisNumber: (application as any).basisNumber || application.externalNumber || '',
        basisDate: (application as any).basisDate ? dayjs((application as any).basisDate) : (application.applicationDate ? dayjs(application.applicationDate) : undefined),
        clientId: application.clientId != null ? Number(application.clientId) : undefined,
        expeditorId: application.expeditorId != null ? Number(application.expeditorId) : undefined,
        shipperId: application.shipperId != null ? Number(application.shipperId) : undefined,
        consigneeId: application.consigneeId != null ? Number(application.consigneeId) : undefined,
        responsibleLogistId: application.responsibleLogistId != null ? Number(application.responsibleLogistId) : (currentUser?.id ? Number(currentUser.id) : undefined),
        loadingAddress: application.loadingAddress || '',
        loadingCity: (application as any).loadingCity || '',
        loadingContactPerson: application.loadingContactPerson || '',
        loadingPhone: application.loadingPhone || '',
        unloadingAddress: application.unloadingAddress || '',
        unloadingCity: (application as any).unloadingCity || '',
        unloadingContactPerson: application.unloadingContactPerson || '',
        unloadingPhone: application.unloadingPhone || '',
        plannedLoadingDate: application.plannedLoadingDate ? dayjs(application.plannedLoadingDate) : null,
        plannedUnloadingDate: application.plannedUnloadingDate ? dayjs(application.plannedUnloadingDate) : null,
        totalCostWithVAT: application.totalCostWithVAT || undefined,
        requiredConditions: application.requiredConditions || '',
        // Поля основания заявки
        applicationBasis: (application as any).applicationBasis || 'CLIENT_CONTRACT',
        contractId: (application as any).contractId ?? undefined,
        // Финансовые поля
        paymentMethod: (application as any).paymentMethod || undefined,
        prepaymentCondition: (application as any).prepaymentCondition || undefined,
        prepaymentPercent: (application as any).prepaymentPercent ?? undefined,
        mainPaymentPercent: (application as any).mainPaymentPercent ?? ((application as any).prepaymentPercent !== null && (application as any).prepaymentPercent !== undefined ? (100 - Number((application as any).prepaymentPercent)) : undefined),
        prepaymentDelayDays: (application as any).prepaymentDelayDays ?? undefined,
        mainPaymentDelayDays: (application as any).mainPaymentDelayDays ?? undefined
      };

      console.log('📋 Form values to set:', formValues);
      console.log('🔍 КОНТРАГЕНТЫ В formValues:', {
        clientId: { value: formValues.clientId, type: typeof formValues.clientId },
        expeditorId: { value: formValues.expeditorId, type: typeof formValues.expeditorId },
        shipperId: { value: formValues.shipperId, type: typeof formValues.shipperId },
        consigneeId: { value: formValues.consigneeId, type: typeof formValues.consigneeId }
      });
      console.log('🔍 ФИНАНСОВЫЕ ПОЛЯ В formValues:', {
        paymentMethod: formValues.paymentMethod,
        prepaymentCondition: formValues.prepaymentCondition,
        prepaymentPercent: formValues.prepaymentPercent,
        mainPaymentPercent: formValues.mainPaymentPercent,
        prepaymentDelayDays: formValues.prepaymentDelayDays,
        mainPaymentDelayDays: formValues.mainPaymentDelayDays
      });
      
      // Устанавливаем значения в форму
      form.setFieldsValue(formValues);
      
      // Если mainPaymentPercent не задан, но задан prepaymentPercent, вычисляем его
      if (formValues.mainPaymentPercent === undefined && formValues.prepaymentPercent !== undefined && formValues.prepaymentPercent !== null) {
        const calculatedMainPayment = 100 - Number(formValues.prepaymentPercent);
        form.setFieldValue('mainPaymentPercent', calculatedMainPayment);
      }
      
      // Устанавливаем локальные состояния для Select компонентов
      setSelectedClientId(application.clientId || null);
      setSelectedExpeditorId(application.expeditorId || null);
      setSelectedShipperId(application.shipperId || null);
      setSelectedConsigneeId(application.consigneeId || null);
      
      // Проверяем что значения установились
      setTimeout(() => {
        const currentValues = form.getFieldsValue();
        console.log('✅ Current form values after setting:', currentValues);
        
        // Дополнительная проверка Select значений
        console.log('🔍 Select values check:', {
          clientId: currentValues.clientId,
          expeditorId: currentValues.expeditorId,
          shipperId: currentValues.shipperId,
          consigneeId: currentValues.consigneeId
        });
        
        // 🔍 ПРОВЕРКА НА ПОТЕРЮ ДАННЫХ ПРИ УСТАНОВКЕ В ФОРМУ
        console.log('🔍 ПРОВЕРКА ПОТЕРИ ДАННЫХ:', {
          'clientId сохранился': currentValues.clientId === formValues.clientId,
          'expeditorId сохранился': currentValues.expeditorId === formValues.expeditorId,
          'shipperId сохранился': currentValues.shipperId === formValues.shipperId,
          'consigneeId сохранился': currentValues.consigneeId === formValues.consigneeId,
          'Различия': {
            client: currentValues.clientId !== formValues.clientId ? 
              `${formValues.clientId} → ${currentValues.clientId}` : 'OK',
            expeditor: currentValues.expeditorId !== formValues.expeditorId ? 
              `${formValues.expeditorId} → ${currentValues.expeditorId}` : 'OK',
            shipper: currentValues.shipperId !== formValues.shipperId ? 
              `${formValues.shipperId} → ${currentValues.shipperId}` : 'OK',
            consignee: currentValues.consigneeId !== formValues.consigneeId ? 
              `${formValues.consigneeId} → ${currentValues.consigneeId}` : 'OK'
          }
        });
        
        // Проверка соответствия справочникам
        console.log('📚 Reference data check:', {
          clientExists: clients.find(c => c.id === currentValues.clientId),
          expeditorExists: expeditors.find(e => e.id === currentValues.expeditorId),
          shipperExists: shippers.find(s => s.id === currentValues.shipperId),
          consigneeExists: consignees.find(c => c.id === currentValues.consigneeId)
        });
      }, 100);
      
      // Сохраняем данные первого шага для редактирования
      setStep1Data(formValues);
        console.log('🔍 step1Data установлен с formValues:', formValues);

      setConditions(Array.isArray(application.conditions) ? application.conditions : []);
      
      // Загрузить грузы если есть
      if (application.cargo && application.cargo.length > 0) {
        console.log('🚀 НАЧАЛО ЗАГРУЗКИ ГРУЗОВ');
        console.log('📦 Loading cargo items:', application.cargo.length);
        console.log('🔍 application.cargo:', application.cargo);
        console.log('🔍 Raw cargo data from server:', application.cargo);
        
        // 🔍 🔍 🔍 ДОПОЛНИТЕЛЬНАЯ ДИАГНОСТИКА: Что получает компонент от RTK Query
        console.log('🔍 🔍 🔍 ДАННЫЕ ГРУЗА В КОМПОНЕНТЕ (ДО обработки):');
        application.cargo.forEach((cargo, index) => {
          console.log(`  Груз ${index} (${cargo.name}) в компоненте:`, {
            id: cargo.id,
            lengthM: { value: cargo.lengthM, type: typeof cargo.lengthM, isNull: cargo.lengthM === null, isUndefined: cargo.lengthM === undefined },
            widthM: { value: cargo.widthM, type: typeof cargo.widthM, isNull: cargo.widthM === null, isUndefined: cargo.widthM === undefined },
            heightM: { value: cargo.heightM, type: typeof cargo.heightM, isNull: cargo.heightM === null, isUndefined: cargo.heightM === undefined },
            volumeM3: { value: cargo.volumeM3, type: typeof cargo.volumeM3, isNull: cargo.volumeM3 === null, isUndefined: cargo.volumeM3 === undefined }
          });
        });
        console.log('🔍 Raw cargo data DETAILED:', application.cargo.map((cargo, index) => ({
          index,
          name: cargo.name,
          lengthM: { raw: cargo.lengthM, type: typeof cargo.lengthM, parsed: parseDecimal(cargo.lengthM) },
          widthM: { raw: cargo.widthM, type: typeof cargo.widthM, parsed: parseDecimal(cargo.widthM) },
          heightM: { raw: cargo.heightM, type: typeof cargo.heightM, parsed: parseDecimal(cargo.heightM) },
          volumeM3: { raw: cargo.volumeM3, type: typeof cargo.volumeM3, parsed: parseDecimal(cargo.volumeM3) }
        })));
        
        const cargoItems = application.cargo.map((cargo, index) => {
          console.log(`📦 Cargo item ${index}:`, {
            name: cargo.name,
            quantity: { value: cargo.quantity, type: typeof cargo.quantity, parsed: parseDecimal(cargo.quantity) },
            weightPerItem: { value: cargo.weightPerItem, type: typeof cargo.weightPerItem, parsed: parseDecimal(cargo.weightPerItem) },
            totalWeightKg: { value: cargo.totalWeightKg, type: typeof cargo.totalWeightKg, parsed: parseDecimal(cargo.totalWeightKg) },
            lengthM: { value: cargo.lengthM, type: typeof cargo.lengthM, parsed: parseDecimal(cargo.lengthM) },
            widthM: { value: cargo.widthM, type: typeof cargo.widthM, parsed: parseDecimal(cargo.widthM) },
            heightM: { value: cargo.heightM, type: typeof cargo.heightM, parsed: parseDecimal(cargo.heightM) },
            volumeM3: { value: cargo.volumeM3, type: typeof cargo.volumeM3, parsed: parseDecimal(cargo.volumeM3) }
          });
          
          // 🔍 ДЕТАЛЬНАЯ ДИАГНОСТИКА: Проверяем каждый тип данных отдельно
          const lengthM = parseDecimal(cargo.lengthM);
          const widthM = parseDecimal(cargo.widthM);
          const heightM = parseDecimal(cargo.heightM);
          
          console.log(`🔍 Габариты для груза ${index} (${cargo.name}):`, {
            raw: { lengthM: cargo.lengthM, widthM: cargo.widthM, heightM: cargo.heightM },
            parsed: { lengthM, widthM, heightM },
            types: { 
              lengthM: typeof cargo.lengthM, 
              widthM: typeof cargo.widthM, 
              heightM: typeof cargo.heightM 
            }
          });
          
          const parsedItem = {
          key: index.toString(),
            id: cargo.id, // ✅ ДОБАВЛЯЕМ ID груза из БД!
          name: cargo.name,
          description: cargo.description || '',
            quantity: parseRequiredDecimal(cargo.quantity),
            weightPerItem: parseRequiredDecimal(cargo.weightPerItem),
            lengthM: lengthM,
            widthM: widthM,
            heightM: heightM,
            totalWeight: parseRequiredDecimal(cargo.totalWeightKg),
            totalVolume: parseDecimal(cargo.volumeM3),
          packagingType: cargo.packagingType || '',
          cargoCharacter: cargo.cargoCharacter || 'обычный'
            ,isLocal: false
          };
          
          console.log(`✅ Parsed cargo item ${index}:`, parsedItem);
          console.log(`🔍 ID груза в parsedItem:`, { id: parsedItem.id, type: typeof parsedItem.id });
          console.log(`🔍 FINAL габариты в parsedItem:`, {
            lengthM: { value: parsedItem.lengthM, type: typeof parsedItem.lengthM },
            widthM: { value: parsedItem.widthM, type: typeof parsedItem.widthM },
            heightM: { value: parsedItem.heightM, type: typeof parsedItem.heightM }
          });
          return parsedItem;
        });
        
        setCargoList(cargoItems);
        console.log('✅ Cargo items loaded:', cargoItems.length);
        console.log('🔍 Processed cargo items:', cargoItems);
        console.log('🔍 FINAL cargoList state check:', cargoItems.map((item, index) => ({
          index,
          id: { value: item.id, type: typeof item.id, hasId: !!item.id },
          name: item.name,
          dimensions: {
            lengthM: { value: item.lengthM, type: typeof item.lengthM },
            widthM: { value: item.widthM, type: typeof item.widthM },
            heightM: { value: item.heightM, type: typeof item.heightM }
          }
        })));
        
        // 🔍 ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: Логируем каждый груз отдельно
        cargoItems.forEach((item, index) => {
          console.log(`🔍 Груз ${index} в cargoItems:`, {
            id: { value: item.id, type: typeof item.id, hasId: !!item.id },
            name: item.name,
            key: item.key
          });
        });
      } else {
        console.log('📦 No cargo items to load');
        setCargoList([]);
      }
    } else if (!isLoadingApplication && !hasUserData) {
      console.log('🆕 Setting form for new application (no application data)');
      // Для новой заявки установим текущую дату ТОЛЬКО если пользователь еще ничего не ввел
      form.setFieldsValue({
        applicationDate: dayjs(),
        applicationBasis: 'CLIENT_CONTRACT',
        responsibleLogistId: currentUser?.id ? Number(currentUser.id) : undefined
      });
      setCargoList([]);
      setConditions([]);
      setStep1Data({});
    } else if (!isLoadingApplication && hasUserData) {
      console.log('🔄 New application mode but user has data, preserving existing form state');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    propApplication, 
    fetchedApplication, 
    form,
    isLoadingApplication, 
    applicationId, 
    isLoadingReferences,
    hasReferenceErrors
    // Намеренно исключаем clients, expeditors, shippers, consignees из зависимостей
    // чтобы предотвратить повторные срабатывания useEffect при загрузке справочников
    // что приводило к потере пользовательских данных
  ]);

  // Дополнительная страховка: когда справочники догрузились, повторно применяем значения контрагентов
  useEffect(() => {
    if (!visible) return;
    const effectiveApp = propApplication || fetchedApplication;
    if (!effectiveApp) return;
    if (isLoadingClients || isLoadingExpeditors || isLoadingShippers || isLoadingConsignees) return;

    const current = form.getFieldsValue();
    const needApply =
      (current.clientId === undefined && effectiveApp.clientId !== undefined) ||
      (current.expeditorId === undefined && effectiveApp.expeditorId !== undefined) ||
      (current.shipperId === undefined && effectiveApp.shipperId !== undefined) ||
      (current.consigneeId === undefined && effectiveApp.consigneeId !== undefined);

    if (needApply) {
      console.log('♻️ Re-applying contractor values after references load');
      setTimeout(() => {
        form.setFieldsValue({
          clientId: effectiveApp.clientId != null ? Number(effectiveApp.clientId) : undefined,
          expeditorId: effectiveApp.expeditorId != null ? Number(effectiveApp.expeditorId) : undefined,
          shipperId: effectiveApp.shipperId != null ? Number(effectiveApp.shipperId) : undefined,
          consigneeId: effectiveApp.consigneeId != null ? Number(effectiveApp.consigneeId) : undefined,
        });
        
        // Также обновляем локальные состояния
        setSelectedClientId(effectiveApp.clientId || null);
        setSelectedExpeditorId(effectiveApp.expeditorId || null);
        setSelectedShipperId(effectiveApp.shipperId || null);
        setSelectedConsigneeId(effectiveApp.consigneeId || null);
      }, 0);
    }
  }, [
    visible,
    propApplication,
    fetchedApplication,
    isLoadingClients,
    isLoadingExpeditors,
    isLoadingShippers,
    isLoadingConsignees,
    form
  ]);

  // Повторное сопоставление контрагентов после загрузки справочников
  useEffect(() => {
    if (!pendingNames || isLoadingClients || isLoadingExpeditors || isLoadingShippers || isLoadingConsignees) return;
    const normalize = (s?: string) => {
      const val = (s || '').toString().toLowerCase();
      return val
        .replace(/[«»"']/g, '')
        .replace(/\b(ооо|оао|ао|пао|зао|ип|ao|oao|ooo|pao|zao)\b/g, '')
        .replace(/\b(общество\s+с\s+ограниченной\s+ответственностью)\b/g, '')
        .replace(/[^a-zа-я0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    };
    const similarity = (a: string, b: string): number => {
      const ta = normalize(a).split(/\s+/).filter(Boolean);
      const tb = normalize(b).split(/\s+/).filter(Boolean);
      if (ta.length === 0 || tb.length === 0) return 0;
      const setA = new Set(ta);
      const setB = new Set(tb);
      let inter = 0;
      setA.forEach(t => { if (setB.has(t)) inter += 1; });
      const union = new Set([...ta, ...tb]).size;
      return inter / union;
    };
    const softPick = <T extends { id: number; name: string }>(items: T[], targetName?: string): T | undefined => {
      if (!targetName || !items || items.length === 0) return undefined;
      let best: { item: T; score: number } | undefined;
      items.forEach(item => {
        const score = similarity(item.name, targetName);
        if (!best || score > best.score) best = { item, score };
      });
      return best && best.score >= 0.4 ? best.item : undefined;
    };
    const n = pendingNames;
    if (n.clientName && clients?.length) {
      const match = clients.find(c => normalize(c.name) === normalize(n.clientName)) || softPick(clients, n.clientName);
      if (match) {
        setSelectedClientId(match.id);
        form.setFieldValue('clientId', String(match.id));
      }
    }
    if (n.expeditorName && expeditors?.length) {
      const match = expeditors.find(c => normalize(c.name) === normalize(n.expeditorName)) || softPick(expeditors, n.expeditorName);
      if (match) {
        setSelectedExpeditorId(match.id);
        form.setFieldValue('expeditorId', String(match.id));
      }
    }
    if (n.shipperName && shippers?.length) {
      const match = shippers.find(c => normalize(c.name) === normalize(n.shipperName)) || softPick(shippers, n.shipperName);
      if (match) {
        setSelectedShipperId(match.id);
        form.setFieldValue('shipperId', String(match.id));
      }
    }
    if (n.consigneeName && consignees?.length) {
      const match = consignees.find(c => normalize(c.name) === normalize(n.consigneeName)) || softPick(consignees, n.consigneeName);
      if (match) {
        setSelectedConsigneeId(match.id);
        form.setFieldValue('consigneeId', String(match.id));
      }
    }
    setPendingNames(null);
  }, [pendingNames, isLoadingClients, isLoadingExpeditors, isLoadingShippers, isLoadingConsignees, clients, expeditors, shippers, consignees, form]);

  // Обработчики для единого модального окна создания контрагента
  const openContractorModal = (role: ContractorRole, title: string) => {
    setContractorDefaultRoles([role]);
    setContractorModalVisible(true);
  };

  // Обработчик успешного создания контрагента
  const handleContractorCreated = (contractor: Contractor) => {
    console.log('✅ Contractor created:', contractor);
    
    // Автоматически выбираем созданного контрагента в соответствующих полях
    // НЕ заполняем адреса, чтобы не перезаписывать данные из импорта
    contractor.roles.forEach((role) => {
      switch (role) {
        case 'client':
          form.setFieldValue('clientId', contractor.id);
          break;
        case 'expeditor':
          form.setFieldValue('expeditorId', contractor.id);
          break;
        case 'shipper':
          setSelectedShipperId(contractor.id);
          form.setFieldValue('shipperId', contractor.id);
          // НЕ заполняем loadingAddress, чтобы не перезаписывать данные из импорта
          break;
        case 'consignee':
          setSelectedConsigneeId(contractor.id);
          form.setFieldValue('consigneeId', contractor.id);
          // НЕ заполняем unloadingAddress, чтобы не перезаписывать данные из импорта
          break;
        default:
          break;
      }
    });
  };

  const handleBackToStep1 = () => {
    console.log('🔙 Returning to step 1...');
    console.log('📋 Step1Data to restore:', step1Data);
    
    // Восстанавливаем только не-контрагентские поля
    if (step1Data && Object.keys(step1Data).length > 0) {
      console.log('✅ Restoring step1 data to form...');
      console.log('🔍 step1Data для восстановления:', step1Data);
      form.setFieldsValue(normalizeStep1FormValuesForRestore(step1Data as Record<string, unknown>));
      
      // Проверяем что данные восстановились
      setTimeout(() => {
        const restoredValues = form.getFieldsValue();
        console.log('🔍 Form values after restoration:', {
          clientId: restoredValues.clientId,
          expeditorId: restoredValues.expeditorId,
          shipperId: restoredValues.shipperId,
          consigneeId: restoredValues.consigneeId,
          externalNumber: restoredValues.externalNumber
        });
      }, 100);
    } else {
      console.log('⚠️ No step1Data to restore');
    }
    
    setCurrentStep(0);
  };



  const handleStep1Submit = () => {
    console.log('🚀 handleStep1Submit started');
    
    // ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: убеждаемся что все важные значения на месте перед валидацией
    const preValidationCheck = form.getFieldsValue();
    console.log('🔍 Pre-validation form state check:', {
      basisNumber: preValidationCheck.basisNumber,
      basisDate: preValidationCheck.basisDate,
      clientId: preValidationCheck.clientId,
      loadingAddress: preValidationCheck.loadingAddress,
      unloadingAddress: preValidationCheck.unloadingAddress,
      plannedLoadingDate: preValidationCheck.plannedLoadingDate,
      plannedUnloadingDate: preValidationCheck.plannedUnloadingDate
    });
    
    // Если clientId отсутствует, попробуем восстановить из нескольких источников
    if (!preValidationCheck.clientId) {
      console.log('⚠️ ClientId missing, attempting recovery...');
      
      let recoveredClientId = null;
      
      // Попытка 1: восстановить из отдельного состояния
      if (selectedClientId) {
        recoveredClientId = selectedClientId;
        console.log('✅ Recovered clientId from state:', recoveredClientId);
      }
      // Попытка 2: восстановить из step1Data
      else if (step1Data?.clientId) {
        recoveredClientId = step1Data.clientId;
        console.log('✅ Recovered clientId from step1Data:', recoveredClientId);
      }
      
      if (recoveredClientId) {
        form.setFieldValue('clientId', recoveredClientId);
        console.log('🔄 Set recovered clientId:', recoveredClientId);
      } else {
        console.error('❌ Unable to recover clientId from any source');
      }
    }
    
    // Используем только встроенную валидацию Ant Design
    form.validateFields([
      'basisNumber', 
      'basisDate', 
      'clientId', 
      'loadingAddress',
      'unloadingAddress',
      'plannedLoadingDate', 
      'plannedUnloadingDate'
    ])
      .then((values) => {
        console.log('🔍 Validation passed, validated values:', values);

        const formValuesAll = form.getFieldsValue(true);
        setStep1Data((prev: Record<string, unknown>) => ({
          ...prev,
          ...formValuesAll
        }));

        console.log('📋 Complete form data collected (getFieldsValue true):', {
          loadingCity: formValuesAll.loadingCity,
          unloadingCity: formValuesAll.unloadingCity,
          externalNumber: formValuesAll.externalNumber,
          clientId: formValuesAll.clientId,
          expeditorId: formValuesAll.expeditorId,
          shipperId: formValuesAll.shipperId,
          consigneeId: formValuesAll.consigneeId,
          applicationDate: formValuesAll.applicationDate?.format?.('YYYY-MM-DD'),
          plannedLoadingDate: formValuesAll.plannedLoadingDate?.format?.('YYYY-MM-DD'),
          plannedUnloadingDate: formValuesAll.plannedUnloadingDate?.format?.('YYYY-MM-DD'),
          totalCostWithVAT: formValuesAll.totalCostWithVAT,
          requiredConditions: formValuesAll.requiredConditions
        });

        const contractorValidation = {
          hasClient: !!formValuesAll.clientId,
          expeditorId: formValuesAll.expeditorId,
          shipperId: formValuesAll.shipperId,
          consigneeId: formValuesAll.consigneeId
        };
        console.log('🏢 Contractor validation:', contractorValidation);

        setSelectedClientId(formValuesAll.clientId != null ? Number(formValuesAll.clientId) : null);
        setSelectedExpeditorId(formValuesAll.expeditorId != null ? Number(formValuesAll.expeditorId) : null);
        setSelectedShipperId(formValuesAll.shipperId != null ? Number(formValuesAll.shipperId) : null);
        setSelectedConsigneeId(formValuesAll.consigneeId != null ? Number(formValuesAll.consigneeId) : null);
        console.log('✅ Step 1 data saved successfully (merged into step1Data):', formValuesAll);

        setCurrentStep(1);
      })
      .catch((errorInfo) => {
        console.error('❌ Validation failed - Detailed analysis:', {
          errorInfo,
          errorFields: errorInfo.errorFields,
          errorFieldsCount: errorInfo.errorFields?.length || 0,
          errorFieldsDetails: errorInfo.errorFields?.map((field: any) => ({
            name: field.name,
            errors: field.errors,
            value: field.value
          })) || []
        });
        
        // Дополнительная диагностика - проверим текущие значения формы
        const currentFormValues = form.getFieldsValue();
        console.error('🔍 Current form values at validation failure:', {
          externalNumber: currentFormValues.externalNumber,
          applicationDate: currentFormValues.applicationDate,
          clientId: currentFormValues.clientId,
          loadingAddress: currentFormValues.loadingAddress,
          unloadingAddress: currentFormValues.unloadingAddress,
          plannedLoadingDate: currentFormValues.plannedLoadingDate,
          plannedUnloadingDate: currentFormValues.plannedUnloadingDate,
          allValues: currentFormValues
        });
        
        // Проверим статус каждого обязательного поля
        const requiredFields = ['basisNumber', 'basisDate', 'clientId', 'loadingAddress', 'unloadingAddress', 'plannedLoadingDate', 'plannedUnloadingDate'];
        const fieldValidationStatus = requiredFields.map(fieldName => ({
          field: fieldName,
          value: currentFormValues[fieldName],
          isEmpty: !currentFormValues[fieldName],
          type: typeof currentFormValues[fieldName],
          isDayjs: currentFormValues[fieldName]?.format ? 'yes' : 'no'
        }));
        console.error('📋 Field-by-field validation status:', fieldValidationStatus);
        
        const missingFields = errorInfo.errorFields?.map((field: any) => field.name[0]) || [];
        const detailedErrors = errorInfo.errorFields?.map((field: any) => `${field.name[0]}: ${field.errors.join(', ')}`).join('; ') || 'Неизвестные ошибки';
        
        console.error('💥 Missing fields summary:', missingFields);
        console.error('💥 Detailed errors:', detailedErrors);
        
        message.error(`Ошибки валидации: ${detailedErrors}`);
      });
  };

  const handleAddCargo = () => {
    setEditingCargo(null);
    cargoForm.resetFields();
    setCargoModalVisible(true);
  };

  const handleEditCargo = (cargo: CargoItem) => {
    console.log('🔍 Editing cargo item:', cargo);
    console.log('📏 Cargo dimensions:', {
      lengthM: { value: cargo.lengthM, type: typeof cargo.lengthM },
      widthM: { value: cargo.widthM, type: typeof cargo.widthM },
      heightM: { value: cargo.heightM, type: typeof cargo.heightM }
    });
    
    setEditingCargo(cargo);
    cargoForm.setFieldsValue(cargo);
    
    // Проверяем что значения установились в форму
    setTimeout(() => {
      const formValues = cargoForm.getFieldsValue();
      console.log('✅ Form values after setFieldsValue:', formValues);
      console.log('📏 Form dimensions after setFieldsValue:', {
        lengthM: { value: formValues.lengthM, type: typeof formValues.lengthM },
        widthM: { value: formValues.widthM, type: typeof formValues.widthM },
        heightM: { value: formValues.heightM, type: typeof formValues.heightM }
      });
    }, 100);
    
    setCargoModalVisible(true);
  };

  const handleDeleteCargo = (key: string) => {
    Modal.confirm({
      title: 'Удаление груза',
      content: 'Вы уверены, что хотите удалить этот груз?',
      onOk: () => {
        setCargoList(cargoList.filter(item => item.key !== key));
        message.success('Груз удален');
      }
    });
  };

  const handleImportCargo = (importedCargo: CargoImportRow[]) => {
    const newCargoItems: CargoItem[] = importedCargo.map((cargo, index) => {
      const { totalWeight, totalVolume } = calculateTotals({
        quantity: cargo.quantity,
        weightPerItem: cargo.weightPerItem,
        lengthM: cargo.lengthM,
        widthM: cargo.widthM,
        heightM: cargo.heightM
      });

      return {
        key: `imported_${Date.now()}_${index}`,
        name: cargo.name,
        description: cargo.description || '',
        quantity: cargo.quantity,
        weightPerItem: cargo.weightPerItem,
        lengthM: cargo.lengthM,
        widthM: cargo.widthM,
        heightM: cargo.heightM,
        totalWeight: totalWeight ?? 0,
        totalVolume: totalVolume ?? 0,
        packagingType: cargo.packagingType || '',
        cargoCharacter: cargo.cargoCharacter || ''
      };
    });

    setCargoList(prev => [...prev, ...newCargoItems]);
    setCargoImportModalVisible(false);
    message.success(`Добавлено ${newCargoItems.length} грузов из файла`);
  };

  const calculateTotals = (values: any) => {
    // Проверяем что все размеры заполнены и являются числами
    const quantity = Number(values.quantity) || 0;
    const weightPerItem = values.weightPerItem !== null && values.weightPerItem !== undefined ? Number(values.weightPerItem) : null;
    const lengthM = values.lengthM !== null && values.lengthM !== undefined ? Number(values.lengthM) : null;
    const widthM = values.widthM !== null && values.widthM !== undefined ? Number(values.widthM) : null;
    const heightM = values.heightM !== null && values.heightM !== undefined ? Number(values.heightM) : null;
    
    // Вес: если введён вручную — используем его, иначе рассчитываем из веса места × количество
    let totalWeight = null;
    const manualWeight = values.totalWeight !== null && values.totalWeight !== undefined ? Number(values.totalWeight) : null;
    
    if (manualWeight !== null && !isNaN(manualWeight) && manualWeight > 0) {
      // Используем введённый вручную вес
      totalWeight = manualWeight;
    } else if (weightPerItem !== null && weightPerItem > 0) {
      // Рассчитываем из веса места × количество
      totalWeight = quantity * weightPerItem;
    }
    
    // Объем: если введён вручную — используем его, иначе рассчитываем из габаритов
    let totalVolume = null;
    const manualVolume = values.totalVolume !== null && values.totalVolume !== undefined ? Number(values.totalVolume) : null;
    
    if (manualVolume !== null && !isNaN(manualVolume) && manualVolume > 0) {
      // Используем введённый вручную объём
      totalVolume = manualVolume;
    } else if (lengthM !== null && widthM !== null && heightM !== null) {
      // Рассчитываем из габаритов
      totalVolume = quantity * lengthM * widthM * heightM;
    }
    
    return { 
      totalWeight: totalWeight !== null && !isNaN(totalWeight) ? totalWeight : null, 
      totalVolume: totalVolume !== null && !isNaN(totalVolume) ? totalVolume : null
    };
  };

  const handleCargoSubmit = async () => {
    cargoForm.validateFields()
      .then(async (values) => {
        console.log('📦 Cargo form values before submit:', values);
        console.log('🔍 Габариты в форме перед сохранением:', {
          lengthM: { value: values.lengthM, type: typeof values.lengthM },
          widthM: { value: values.widthM, type: typeof values.widthM },
          heightM: { value: values.heightM, type: typeof values.heightM }
        });
        
        const { totalWeight, totalVolume } = calculateTotals(values);
        console.log('📊 Calculated totals:', { totalWeight, totalVolume });
        
        const newCargo: CargoItem = {
          key: editingCargo ? editingCargo.key : Date.now().toString(),
          ...values,
          totalWeight,
          totalVolume
          ,isLocal: editingCargo ? (editingCargo.id ? false : true) : true
        };
        
        console.log('✅ New cargo item to save:', newCargo);
        console.log('🔍 FINAL габариты в newCargo:', {
          lengthM: { value: newCargo.lengthM, type: typeof newCargo.lengthM },
          widthM: { value: newCargo.widthM, type: typeof newCargo.widthM },
          heightM: { value: newCargo.heightM, type: typeof newCargo.heightM }
        });
        
        console.log('🔍 ПОЛНЫЙ newCargo объект:', newCargo);
        
        // Детальное логирование габаритов
        console.log('🔍 ГАБАРИТЫ В newCargo:', {
          lengthM: { value: newCargo.lengthM, type: typeof newCargo.lengthM },
          widthM: { value: newCargo.widthM, type: typeof newCargo.widthM },
          heightM: { value: newCargo.heightM, type: typeof newCargo.heightM }
        });

        if (editingCargo) {
          console.log('🔄 Updating existing cargo (editing mode):', editingCargo.key);
          console.log('🔍 ID груза для обновления (may be undefined for local items):', { id: editingCargo.id, type: typeof editingCargo.id });

          // Если у груза есть ID в БД — обновляем через API
          if (editingCargo.id) {
            try {
              console.log('📡 Отправляем обновление груза в БД, ID:', editingCargo.id);
              await updateCargo({
                id: editingCargo.id,
                data: {
                  name: newCargo.name,
                  description: newCargo.description,
                  quantity: newCargo.quantity,
                  weightPerItem: newCargo.weightPerItem,
                  lengthM: newCargo.lengthM ?? undefined,
                  widthM: newCargo.widthM ?? undefined,
                  heightM: newCargo.heightM ?? undefined,
                  packagingType: newCargo.packagingType,
                  cargoCharacter: newCargo.cargoCharacter
                }
              });
              console.log('✅ Груз успешно обновлен в БД');

              // Обновляем cargoList, сохраняя ID
              const updatedCargoList = cargoList.map(item => 
                item.key === editingCargo.key ? {
                  ...newCargo,
                  id: editingCargo.id
                } : item
              );

              setCargoList(updatedCargoList);

              console.log('🔄 Обновленный cargoList (from DB update):', updatedCargoList.map(item => ({ key: item.key, id: item.id, name: item.name, hasId: !!item.id })));
          message.success('Груз обновлен');
            } catch (error) {
              console.error('❌ Ошибка при обновлении груза в БД:', error);
              message.error('Ошибка при обновлении груза в БД');
              return;
            }
        } else {
            // Если у груза нет ID — это локально созданный элемент, обновляем только локально
            console.log('⚠️ Локальный груз без ID — обновляем только локальный state');
            const updatedCargoList = cargoList.map(item => 
              item.key === editingCargo.key ? {
                ...newCargo,
                // сохраняем отсутствие id
                id: undefined
              } : item
            );

            setCargoList(updatedCargoList);
            console.log('🔄 Обновленный cargoList (local):', updatedCargoList.map(item => ({ key: item.key, id: item.id, name: item.name, hasId: !!item.id })));
            message.success('Груз обновлен (локально)');
          }
        } else {
          console.log('➕ Adding new cargo');
          setCargoList([...cargoList, newCargo]);
          message.success('Груз добавлен');
        }

        setCargoModalVisible(false);
        cargoForm.resetFields();
      })
      .catch((errorInfo) => {
        console.error('❌ Cargo form validation failed:', errorInfo);
        message.error('Заполните все обязательные поля груза');
      });
  };

  const openConditionModal = (condition?: ApplicationCondition, index?: number) => {
    setEditingConditionIndex(index ?? null);
    conditionForm.resetFields();
    conditionForm.setFieldsValue(condition || {});
    setConditionModalVisible(true);
  };

  const handleSaveCondition = async () => {
    try {
      const values = await conditionForm.validateFields();
      const type = values.type as ApplicationConditionType;
      const next: ApplicationCondition = {
        type,
        calculationType: values.calculationType || null,
        valueType: values.valueType || null,
        value: values.value ?? null,
        thresholdDays: values.thresholdDays ?? null,
        deadlineDays: values.deadlineDays ?? null,
        comment: values.comment?.trim() || null,
      };

      if (type === 'LOADING_FAILURE' || type === 'UNLOADING_FAILURE' || type === 'VEHICLE_CHANGE') {
        next.valueType = null;
        next.thresholdDays = null;
        next.deadlineDays = null;
      } else if (type === 'LATE_LOADING' || type === 'LATE_UNLOADING') {
        next.calculationType = 'per_day';
        next.deadlineDays = null;
      } else if (type === 'DOCUMENTS') {
        next.thresholdDays = null;
        const hasPenalty = next.calculationType || next.valueType || next.value !== null;
        if (!hasPenalty) {
          next.calculationType = null;
          next.valueType = null;
          next.value = null;
        } else if (!next.calculationType || !next.valueType || next.value === null) {
          message.error('Заполните все поля штрафа по оригиналам документов или оставьте штраф пустым');
          return;
        }
      } else {
        next.calculationType = null;
        next.valueType = null;
        next.thresholdDays = null;
        next.deadlineDays = null;
      }

      setConditions((prev) => {
        if (editingConditionIndex === null) return [...prev, next];
        return prev.map((item, index) => (index === editingConditionIndex ? { ...item, ...next } : item));
      });
      setConditionModalVisible(false);
      conditionForm.resetFields();
    } catch {
      message.error('Заполните обязательные поля условия');
    }
  };

  const handleDeleteCondition = (indexToDelete: number) => {
    setConditions((prev) => prev.filter((_, index) => index !== indexToDelete));
  };

  const handleCancel = () => {
    console.log('🔄 Canceling form, resetting all data');
    // Не очищаем локально форму — черновик уже сохранен
    saveDraft();
    form.resetFields();
    setCurrentStep(0);
            setCargoList([]);
            setConditions([]);
        setStep1Data({});
        console.log('🔍 step1Data сброшен при инициализации');
        setSelectedClientId(null);
    setLoading(false);
    setCargoModalVisible(false);
    setContractorModalVisible(false);
    onCancel();
  };

  const handleFinalSubmit = async () => {
    console.log('🚀 handleFinalSubmit STARTED');
    
    if (cargoList.length === 0) {
      console.log('❌ No cargo items found');
      message.error('Добавьте хотя бы один груз');
      return;
    }
    
    console.log('✅ Cargo list is valid, count:', cargoList.length);
    
    // 🔍 ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: Логируем состояние cargoList перед отправкой
    console.log('🔍 🔍 🔍 СОСТОЯНИЕ CARGO LIST ПЕРЕД ОТПРАВКОЙ:', {
      cargoListLength: cargoList.length,
      cargoItems: cargoList.map((item, index) => ({
        index,
        key: item.key,
        id: item.id,
        name: item.name,
        hasId: !!item.id,
        type: typeof item.id
      }))
    });

    const formatDateForApi = (v: any): string | undefined => {
      if (!v) return undefined;
      // dayjs
      if (typeof v?.format === 'function') return v.format('YYYY-MM-DD');
      // Date
      if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString().slice(0, 10);
      // string
      if (typeof v === 'string') {
        // already yyyy-mm-dd
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
        const strict = dayjs(v, ['DD.MM.YYYY', 'D.M.YYYY', 'YYYY-MM-DD'], true);
        if (strict && typeof strict.isValid === 'function' && strict.isValid()) return strict.format('YYYY-MM-DD');
        const loose = dayjs(v);
        return (loose && typeof loose.isValid === 'function' && loose.isValid()) ? loose.format('YYYY-MM-DD') : undefined;
      }
      return undefined;
    };

    // Получаем контрагентов из максимально надёжного источника:
    // 1) отдельные state (selected*), 2) step1Data (сохранённое), 3) form store (если сохранилось)
    const formClientId = form.getFieldValue('clientId');
    const formExpeditorId = form.getFieldValue('expeditorId');
    const formShipperId = form.getFieldValue('shipperId');
    const formConsigneeId = form.getFieldValue('consigneeId');

    const contractorValues = {
      clientId: selectedClientId ?? step1Data?.clientId ?? formClientId,
      expeditorId: selectedExpeditorId ?? step1Data?.expeditorId ?? formExpeditorId,
      shipperId: selectedShipperId ?? step1Data?.shipperId ?? formShipperId,
      consigneeId: selectedConsigneeId ?? step1Data?.consigneeId ?? formConsigneeId,
    };

    // Для остальных полей используем step1Data + форму
    const currentFormValues = form.getFieldsValue();
    const formResponsibleLogistId = form.getFieldValue('responsibleLogistId');
    
    // Получаем финансовые поля из формы (они могут быть на шаге 1, но отображаются условно)
    const financialFields = {
      paymentMethod: form.getFieldValue('paymentMethod') ?? step1Data?.paymentMethod ?? currentFormValues.paymentMethod,
      prepaymentCondition: form.getFieldValue('prepaymentCondition') ?? step1Data?.prepaymentCondition ?? currentFormValues.prepaymentCondition,
      prepaymentPercent: form.getFieldValue('prepaymentPercent') ?? step1Data?.prepaymentPercent ?? currentFormValues.prepaymentPercent,
      mainPaymentPercent: form.getFieldValue('mainPaymentPercent') ?? step1Data?.mainPaymentPercent ?? currentFormValues.mainPaymentPercent,
      prepaymentDelayDays: form.getFieldValue('prepaymentDelayDays') ?? step1Data?.prepaymentDelayDays ?? currentFormValues.prepaymentDelayDays,
      mainPaymentDelayDays: form.getFieldValue('mainPaymentDelayDays') ?? step1Data?.mainPaymentDelayDays ?? currentFormValues.mainPaymentDelayDays
    };
    
    // Получаем responsibleLogistId с правильным приоритетом: форма -> step1Data -> application -> currentUser
    // Важно: проверяем явно на undefined, чтобы не потерять null или 0
    const responsibleLogistIdValue = formResponsibleLogistId !== undefined && formResponsibleLogistId !== null
      ? Number(formResponsibleLogistId)
      : (step1Data?.responsibleLogistId !== undefined && step1Data?.responsibleLogistId !== null
          ? Number(step1Data.responsibleLogistId)
          : (application?.responsibleLogistId != null 
              ? Number(application.responsibleLogistId) 
              : (currentUser?.id ? Number(currentUser.id) : undefined)));
    
    // Получаем поля основания заявки
    const basisFields = {
      applicationBasis: form.getFieldValue('applicationBasis') ?? step1Data?.applicationBasis ?? currentFormValues.applicationBasis ?? 'CLIENT_CONTRACT',
      basisNumber: form.getFieldValue('basisNumber') ?? step1Data?.basisNumber ?? currentFormValues.basisNumber,
      basisDate: form.getFieldValue('basisDate') ?? step1Data?.basisDate ?? currentFormValues.basisDate,
      contractId: form.getFieldValue('contractId') ?? step1Data?.contractId ?? currentFormValues.contractId
    };

    // step1Data после currentFormValues: на шаге грузов форма размонтирована — города и шаг 1 только в step1Data
    const combinedValues = {
      ...currentFormValues,
      ...step1Data,
      ...contractorValues,
      ...financialFields,
      ...basisFields,
      responsibleLogistId: responsibleLogistIdValue
    };

    console.log('FINAL APPLICATION:', combinedValues);

    console.log('🔍 combinedValues debug:', {
      step1Data: step1Data,
      currentFormValues: currentFormValues,
      contractorValues: contractorValues,
      finalCombined: combinedValues
    });

    console.log('📋 Combined values (current + step1Data):', {
      clientId: combinedValues.clientId,
      expeditorId: combinedValues.expeditorId,
      shipperId: combinedValues.shipperId,
      consigneeId: combinedValues.consigneeId,
      basisNumber: combinedValues.basisNumber,
      basisDate: combinedValues.basisDate
    });
    
    console.log('🔍 Data sources comparison:', {
      'From currentForm': {
        clientId: currentFormValues.clientId,
        expeditorId: currentFormValues.expeditorId,
        shipperId: currentFormValues.shipperId,
        consigneeId: currentFormValues.consigneeId
      },
      'From step1Data': {
        clientId: step1Data.clientId,
        expeditorId: step1Data.expeditorId,
        shipperId: step1Data.shipperId,
        consigneeId: step1Data.consigneeId
      },
      'Extracted contractorValues': {
        clientId: contractorValues.clientId,
        expeditorId: contractorValues.expeditorId,
        shipperId: contractorValues.shipperId,
        consigneeId: contractorValues.consigneeId
      },
      'Final combined': {
        clientId: combinedValues.clientId,
        expeditorId: combinedValues.expeditorId,
        shipperId: combinedValues.shipperId,
        consigneeId: combinedValues.consigneeId
      }
    });
    
    // Критическая проверка: клиент должен быть выбран
    if (!combinedValues?.clientId) {
      console.error('❌ CRITICAL: clientId is missing from combined values');
      message.error('Ошибка: не выбран клиент. Заполните поле "Клиент".');
      setCurrentStep(0);
      setLoading(false);
      return;
    }
    
    if (!combinedValues?.basisNumber || !combinedValues?.basisDate) {
      console.error('❌ CRITICAL: Required fields missing from combined values', {
        basisNumber: combinedValues?.basisNumber,
        basisDate: combinedValues?.basisDate
      });
      message.error('Ошибка: укажите номер и дату входящей заявки.');
      setCurrentStep(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log('🔄 Loading state set to true');
    
    try {
      console.log('✅ Form data validation passed, proceeding with form submission...');

      let loadingCityOut =
        combinedValues.loadingCity != null && String(combinedValues.loadingCity).trim()
          ? String(combinedValues.loadingCity).trim()
          : undefined;
      let unloadingCityOut =
        combinedValues.unloadingCity != null && String(combinedValues.unloadingCity).trim()
          ? String(combinedValues.unloadingCity).trim()
          : undefined;
      if (!loadingCityOut && combinedValues.loadingAddress) {
        const hint = await resolveCityHintFromAddressString(String(combinedValues.loadingAddress).trim());
        if (hint) loadingCityOut = hint;
      }
      if (!unloadingCityOut && combinedValues.unloadingAddress) {
        const hint = await resolveCityHintFromAddressString(String(combinedValues.unloadingAddress).trim());
        if (hint) unloadingCityOut = hint;
      }

      console.log('🏗️ Building form data from COMBINED values...');
    const formData: CreateApplicationRequest = {
        externalNumber: combinedValues.basisNumber!,
        applicationDate: formatDateForApi(combinedValues.basisDate)!,
        clientId: combinedValues.clientId != null ? Number(combinedValues.clientId) : (undefined as any),
        expeditorId: combinedValues.expeditorId != null ? Number(combinedValues.expeditorId) : undefined,
        shipperId: combinedValues.shipperId != null ? Number(combinedValues.shipperId) : undefined,
        consigneeId: combinedValues.consigneeId != null ? Number(combinedValues.consigneeId) : undefined,
        responsibleLogistId: combinedValues.responsibleLogistId !== undefined && combinedValues.responsibleLogistId !== null 
          ? Number(combinedValues.responsibleLogistId) 
          : (currentUser?.id ? Number(currentUser.id) : undefined),
        loadingAddress: combinedValues.loadingAddress || undefined,
        loadingCity: loadingCityOut,
        loadingContactPerson: combinedValues.loadingContactPerson || undefined,
        loadingPhone: combinedValues.loadingPhone || undefined,
        unloadingAddress: combinedValues.unloadingAddress || undefined,
        unloadingCity: unloadingCityOut,
        unloadingContactPerson: combinedValues.unloadingContactPerson || undefined,
        unloadingPhone: combinedValues.unloadingPhone || undefined,
        plannedLoadingDate: formatDateForApi(combinedValues.plannedLoadingDate),
        plannedUnloadingDate: formatDateForApi(combinedValues.plannedUnloadingDate),
        totalCostWithVAT: parseDecimal(combinedValues.totalCostWithVAT) ?? undefined,
        requiredConditions: combinedValues.requiredConditions || undefined,
        // Поля основания заявки
        applicationBasis: combinedValues.applicationBasis || 'CLIENT_CONTRACT',
        basisNumber: combinedValues.basisNumber || undefined,
        basisDate: combinedValues.basisDate ? formatDateForApi(combinedValues.basisDate) : undefined,
        clientRate:
          combinedValues.totalCostWithVAT != null && combinedValues.totalCostWithVAT !== ''
            ? (parseDecimal(combinedValues.totalCostWithVAT) ?? undefined)
            : undefined,
        paymentMethod: combinedValues.paymentMethod || undefined,
        prepaymentCondition: combinedValues.prepaymentCondition || undefined,
        prepaymentPercent: combinedValues.prepaymentPercent !== undefined && combinedValues.prepaymentPercent !== null ? Number(combinedValues.prepaymentPercent) : undefined,
        mainPaymentPercent: combinedValues.mainPaymentPercent !== undefined && combinedValues.mainPaymentPercent !== null 
          ? Number(combinedValues.mainPaymentPercent) 
          : (combinedValues.prepaymentPercent !== undefined && combinedValues.prepaymentPercent !== null 
              ? (100 - Number(combinedValues.prepaymentPercent)) 
              : undefined),
        prepaymentDelayDays: combinedValues.prepaymentDelayDays !== undefined && combinedValues.prepaymentDelayDays !== null ? Number(combinedValues.prepaymentDelayDays) : undefined,
        mainPaymentDelayDays: combinedValues.mainPaymentDelayDays !== undefined && combinedValues.mainPaymentDelayDays !== null ? Number(combinedValues.mainPaymentDelayDays) : undefined,
        contractId:
          combinedValues.applicationBasis === 'CLIENT_CONTRACT' &&
          combinedValues.contractId != null &&
          String(combinedValues.contractId).trim() !== ''
            ? Number(combinedValues.contractId)
            : null,
        conditions,
        cargo: cargoList.map(cargo => {
          // 🔍 ДЕТАЛЬНАЯ ДИАГНОСТИКА ID груза
          console.log('🔍 🔍 🔍 ДИАГНОСТИКА ID ГРУЗА:', {
            cargo: {
              id: cargo.id,
          name: cargo.name,
              hasId: !!cargo.id,
              type: typeof cargo.id,
              isNumber: typeof cargo.id === 'number',
              isUndefined: cargo.id === undefined,
              isNull: cargo.id === null
            },
            cargoList: {
              length: cargoList.length,
              allIds: cargoList.map(c => ({ id: c.id, name: c.name, hasId: !!c.id }))
            }
          });
          
          console.log('🔍 Отправляем груз на сервер:', {
            name: cargo.name,
            id: cargo.id,
            hasId: !!cargo.id,
            dimensions: {
              lengthM: { value: cargo.lengthM, type: typeof cargo.lengthM },
              widthM: { value: cargo.widthM, type: typeof cargo.widthM },
              heightM: { value: cargo.heightM, type: typeof cargo.heightM }
            },
            fullCargo: cargo
          });
          
          // Детальное логирование для отладки
          console.log('🔍 ДЕТАЛЬНЫЕ ГАБАРИТЫ для отправки:', {
            name: cargo.name,
            id: cargo.id,
          lengthM: cargo.lengthM,
          widthM: cargo.widthM,
          heightM: cargo.heightM,
            totalVolume: cargo.totalVolume
          });
          
          // Логирование полного объекта для отправки
          console.log('🔍 ПОЛНЫЙ ОБЪЕКТ ДЛЯ ОТПРАВКИ:', {
            name: cargo.name,
            id: cargo.id,
            lengthM: cargo.lengthM,
            widthM: cargo.widthM,
            heightM: cargo.heightM,
            totalVolume: cargo.totalVolume,
            fullCargo: cargo
          });
          
          // Логирование для отладки бэкенда
          console.log('🔍 ОТЛАДКА БЭКЕНДА - что отправляется:', {
            name: cargo.name,
            id: cargo.id,
            lengthM: cargo.lengthM,
            widthM: cargo.widthM,
            heightM: cargo.heightM,
            totalVolume: cargo.totalVolume
          });
          
          // Логирование для отладки бэкенда - детально
          console.log('🔍 ОТЛАДКА БЭКЕНДА - детально:', {
            name: cargo.name,
            id: cargo.id,
            lengthM: { value: cargo.lengthM, type: typeof cargo.lengthM },
            widthM: { value: cargo.widthM, type: typeof cargo.widthM },
            heightM: { value: cargo.heightM, type: typeof cargo.heightM },
            totalVolume: { value: cargo.totalVolume, type: typeof cargo.totalVolume }
          });
          
          // Логируем ID груза для диагностики
          console.log('🔍 Груз для отправки:', { 
            id: cargo.id, 
            name: cargo.name, 
            hasId: !!cargo.id,
            type: typeof cargo.id 
          });
          
          return {
            id: cargo.id, // ✅ ДОБАВЛЯЕМ ID груза для обновления!
            name: cargo.name,
            description: cargo.description || '',
            quantity: cargo.quantity,
            weightPerItem: cargo.weightPerItem,
            lengthM: cargo.lengthM ?? undefined, // null -> undefined для API
            widthM: cargo.widthM ?? undefined,   // null -> undefined для API
            heightM: cargo.heightM ?? undefined, // null -> undefined для API
          totalWeight: cargo.totalWeight, // Используем уже вычисленное значение
            totalVolume: cargo.totalVolume ?? undefined, // null -> undefined для API
          packagingType: cargo.packagingType || '',
          cargoCharacter: cargo.cargoCharacter || ''
          };
        })
      };

      // 🔍 ДИАГНОСТИЧЕСКОЕ ЛОГИРОВАНИЕ для исследования проблемы
      console.log('🔍 ДИАГНОСТИКА КОНТРАГЕНТОВ (FRONTEND - ИСПРАВЛЕНО v2):', {
        'Данные из объединенных значений': {
          clientId: { value: combinedValues.clientId, type: typeof combinedValues.clientId },
          expeditorId: { value: combinedValues.expeditorId, type: typeof combinedValues.expeditorId },
          shipperId: { value: combinedValues.shipperId, type: typeof combinedValues.shipperId },
          consigneeId: { value: combinedValues.consigneeId, type: typeof combinedValues.consigneeId }
        },
        'Данные в formData': {
          clientId: { value: formData.clientId, type: typeof formData.clientId },
          expeditorId: { value: formData.expeditorId, type: typeof formData.expeditorId },
          shipperId: { value: formData.shipperId, type: typeof formData.shipperId },
          consigneeId: { value: formData.consigneeId, type: typeof formData.consigneeId }
        },
        'Проверка на равенство clientId': {
          'expeditorId === clientId': formData.expeditorId === formData.clientId,
          'shipperId === clientId': formData.shipperId === formData.clientId,
          'consigneeId === clientId': formData.consigneeId === formData.clientId
        }
      });

      console.log('Final form data before API call:', formData);
      console.log('About to call createApplication API...');

      let result;
      if (isEdit && application) {
        console.log('Calling updateApplication...');
        result = await updateApplication({ id: application.id, ...formData }).unwrap();
        message.success('Заявка успешно обновлена');
      } else {
        console.log('CREATE APPLICATION PAYLOAD', formData);
        console.log('Calling createApplication...');
        result = await createApplication(formData).unwrap();
        message.success('Заявка успешно создана');
      }

      console.log('✅ API call successful, result:', result);
      
      // Сбрасываем форму после успешного создания/обновления
      if (!isEdit) {
        console.log('🔄 Resetting form after successful creation');
        form.resetFields();
        setCurrentStep(0);
        setCargoList([]);
        setConditions([]);
        setStep1Data({});
        console.log('🔍 step1Data сброшен');
        setSelectedClientId(null);
      }
      
      onSuccess(result);
    } catch (error: any) {
      console.error('❌ Error saving application:', error);
      message.error(error?.data?.error || 'Ошибка сохранения заявки');
    } finally {
      console.log('🔄 Setting loading to false');
      setLoading(false);
    }
  };

  const cargoColumns = [
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: 'Кол-во мест',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Вес места (кг)',
      dataIndex: 'weightPerItem',
      key: 'weightPerItem',
      width: 120,
      align: 'center' as const,
    },
    {
      title: 'Габариты (м)',
      key: 'dimensions',
      width: 150,
      render: (record: CargoItem) => {
        const length = record.lengthM;
        const width = record.widthM;
        const height = record.heightM;
        
        console.log('🔍 Rendering dimensions for cargo:', {
          name: record.name,
          length, width, height,
          types: { length: typeof length, width: typeof width, height: typeof height },
          recordKeys: Object.keys(record),
          fullRecord: record
        });
        
        // Показываем габариты: null -> 0, undefined -> 0, число -> как есть
        const displayLength = length !== null && length !== undefined ? length : 0;
        const displayWidth = width !== null && width !== undefined ? width : 0;
        const displayHeight = height !== null && height !== undefined ? height : 0;
        
        console.log('🔍 DEBUG габариты:', {
          original: { length, width, height },
          display: { displayLength, displayWidth, displayHeight },
          types: { length: typeof length, width: typeof width, height: typeof height }
        });
        
        // Если все габариты равны 0, показываем "—"
        if (displayLength === 0 && displayWidth === 0 && displayHeight === 0) {
          console.log('  → Все габариты равны 0, показываем "—"');
          return '—';
        }
        
        // Показываем габариты (включая 0)
        const result = `${displayLength}×${displayWidth}×${displayHeight}`;
        console.log('✅ Dimensions result:', result, 'from values:', { displayLength, displayWidth, displayHeight });
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{result}</span>
            {record.isLocal && <Tag color="orange">Несохранённый</Tag>}
          </div>
        );
      }
    },
    {
      title: 'Общий вес (кг)',
      dataIndex: 'totalWeight',
      key: 'totalWeight',
      width: 120,
      align: 'center' as const,
    },
    {
      title: 'Общий объем (м³)',
      dataIndex: 'totalVolume',
      key: 'totalVolume',
      width: 130,
      align: 'center' as const,
      render: (volume: number | null) => {
        if (volume === null || volume === undefined || isNaN(volume) || volume === 0) return '—';
        return Number(volume).toFixed(3);
      }
    },
    {
      title: 'Характер груза',
      dataIndex: 'cargoCharacter',
      key: 'cargoCharacter',
      width: 150,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (record: CargoItem) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditCargo(record)}
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteCargo(record.key)}
          />
        </Space>
      ),
    },
  ];

  // Показываем загрузку если данные заявки еще загружаются
  if (isLoadingApplication) {
    return (
      <div className="application-form">
        <Card>
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="loading-spinner mb-4">Загрузка...</div>
              <Text type="secondary">Загрузка данных заявки...</Text>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Если есть ошибка загрузки
  if (hasLoadingError) {
    return (
      <div className="application-form">
        <Card>
          <div className="text-center py-20">
            <Text type="danger">Ошибка загрузки данных заявки</Text>
            <div style={{ marginTop: 16 }}>
              <Button onClick={handleCancel}>Вернуться к списку</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Если это редактирование, но данные не загружены
  if (applicationId && !application && !isLoadingApplication) {
    return (
      <div className="application-form">
        <Card>
          <div className="text-center py-20">
            <Text type="secondary">Заявка не найдена</Text>
            <div style={{ marginTop: 16 }}>
              <Button onClick={handleCancel}>Вернуться к списку</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <Modal
      title={isEdit ? 'Редактирование заявки' : 'Создание новой заявки'}
      open={visible}
      onCancel={handleCancel}
      width={1200}
      footer={null}
      destroyOnClose
    >
    <div className="application-form">
        <div className="mb-6">
          <Row justify="space-between" align="middle">
            <Col>
              <Text type="secondary">
                {isEdit 
                  ? `Заявка №${application?.internalNumber}`
                  : 'Двухэтапный процесс создания заявки на перевозку'
                }
              </Text>
            </Col>
            <Col>
              <Space>
                {applicationId && (
                  <Button 
                    type="dashed" 
                    onClick={testApiCall}
                    size="small"
                  >
                    🧪 Test API
                  </Button>
                )}
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleCancel}
                >
                  Назад к списку
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Steps current={currentStep} className="mb-8">
          <Step title="Основная информация" description="Данные заявки и участники" />
          <Step title="Добавление грузов" description="Характеристики перевозимых грузов" />
        </Steps>

        {currentStep === 0 && (
          <Form
            key={`form-${application?.id || 'new'}`}
            form={form}
            layout="vertical"
            onValuesChange={() => saveDraft()}
          >
            {/* Поля "Номер входящей заявки" и "Дата заявки" убраны - теперь используются поля "Номер" и "Дата" из блока "Основание заявки" */}

            <Row gutter={16}>
              <Col span={24}>
                <Space>
                  <label style={{ color: 'var(--text-secondary)' }}>Импорт из файла (PDF/JPG/PNG):</label>
                  <input
                    id="app-file-import"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      // Сохраняем ссылку на input до await, чтобы избежать null после асинхронности
                      const inputEl = e.currentTarget as HTMLInputElement;
                      const file = inputEl?.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        // По умолчанию используем AUTO: сначала пробуем извлечь текст из PDF (pdf-parse),
                        // и только если текста нет — делаем OCR на бэкенде.
                        const resp = await fetch(`${API_BASE_URL}/applications/import/file`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${localStorage.getItem('logistics_access_token') || ''}` },
                          body: fd
                        });
                        const json = await resp.json();
                        if (json?.rawText) {
                          console.log('📝 rawText sample:', String(json.rawText).slice(0, 800));
                        }
                        if (!resp.ok || !json?.success) {
                          message.error(json?.error || 'Ошибка импорта файла');
                          return;
                        }
                        const d = json.data || {};
                        console.log('📄 OCR import response data:', d);
                        console.log('🔍 Детали OCR данных:', {
                          loadingAddress: d.loadingAddress,
                          loadingAddressType: typeof d.loadingAddress,
                          loadingAddressLength: d.loadingAddress?.length,
                          loadingContactPerson: d.loadingContactPerson,
                          shipperName: d.shipperName,
                          consigneeName: d.consigneeName,
                        });
                        
                        // Безопасная функция для конвертации даты из строки в dayjs
                        const safeDayjs = (dateValue: any, fallback?: any) => {
                          if (!dateValue) return fallback;
                          try {
                            const dayjsValue = dayjs(dateValue, ['DD.MM.YYYY', 'D.M.YYYY', 'YYYY-MM-DD'], true);
                            if (dayjsValue && typeof dayjsValue.isValid === 'function' && dayjsValue.isValid()) {
                              return dayjsValue;
                            }
                          } catch (e) {
                            console.warn('Ошибка при конвертации даты:', e);
                          }
                          return fallback;
                        };
                        
                        // Заполняем основные поля - устанавливаем значения только если они есть в ответе OCR
                        const formValuesToSet: any = {};
                        
                        // Номер и дата заявки устанавливаются в basisNumber и basisDate (новые поля)
                        if (d.externalNumber !== undefined && d.externalNumber !== null && d.externalNumber !== '') {
                          formValuesToSet.basisNumber = d.externalNumber;
                        }
                        if (d.applicationDate !== undefined && d.applicationDate !== null && d.applicationDate !== '') {
                          const dayjsDate = safeDayjs(d.applicationDate);
                          if (dayjsDate) {
                            formValuesToSet.basisDate = dayjsDate;
                          }
                        }
                        if (d.plannedLoadingDate !== undefined && d.plannedLoadingDate !== null && d.plannedLoadingDate !== '') {
                          const dayjsDate = safeDayjs(d.plannedLoadingDate);
                          if (dayjsDate) {
                            formValuesToSet.plannedLoadingDate = dayjsDate;
                          }
                        }
                        if (d.plannedUnloadingDate !== undefined && d.plannedUnloadingDate !== null && d.plannedUnloadingDate !== '') {
                          const dayjsDate = safeDayjs(d.plannedUnloadingDate);
                          if (dayjsDate) {
                            formValuesToSet.plannedUnloadingDate = dayjsDate;
                          }
                        }
                        // Адрес погрузки - проверяем, что это не пустая строка и не только пробелы
                        if (d.loadingAddress !== undefined && d.loadingAddress !== null && String(d.loadingAddress).trim() !== '') {
                          formValuesToSet.loadingAddress = String(d.loadingAddress).trim();
                        }
                        if (d.unloadingAddress !== undefined && d.unloadingAddress !== null && String(d.unloadingAddress).trim() !== '') {
                          formValuesToSet.unloadingAddress = String(d.unloadingAddress).trim();
                        }
                        // Контактное лицо с погрузки - проверяем, что это не пустая строка
                        if (d.loadingContactPerson !== undefined && d.loadingContactPerson !== null && String(d.loadingContactPerson).trim() !== '') {
                          formValuesToSet.loadingContactPerson = String(d.loadingContactPerson).trim();
                        }
                        if (d.unloadingContactPerson !== undefined && d.unloadingContactPerson !== null && d.unloadingContactPerson !== '') {
                          formValuesToSet.unloadingContactPerson = d.unloadingContactPerson;
                        }
                        if (d.loadingPhone !== undefined && d.loadingPhone !== null && d.loadingPhone !== '') {
                          formValuesToSet.loadingPhone = d.loadingPhone;
                        }
                        if (d.unloadingPhone !== undefined && d.unloadingPhone !== null && d.unloadingPhone !== '') {
                          formValuesToSet.unloadingPhone = d.unloadingPhone;
                        }
                        if (d.totalCostWithVAT !== undefined && d.totalCostWithVAT !== null) {
                          formValuesToSet.totalCostWithVAT = d.totalCostWithVAT;
                        }
                        if (d.requiredConditions !== undefined && d.requiredConditions !== null && d.requiredConditions !== '') {
                          formValuesToSet.requiredConditions = d.requiredConditions;
                        }
                        
                        console.log('📝 Устанавливаем значения в форму:', formValuesToSet);
                        form.setFieldsValue(formValuesToSet);

                        // Попробуем сопоставить распознанные названия контрагентов со справочниками
                        try {
                          const normalize = (s?: string) => {
                            const val = (s || '').toString().toLowerCase();
                            // Удаляем организационно-правовые формы и кавычки, выравниваем знаки
                            return val
                              .replace(/[«»"']/g, '')
                              .replace(/\b(ооо|оао|ао|пао|зао|ип|ao|oao|ooo|pao|zao)\b/g, '')
                              .replace(/\b(общество\s+с\s+ограниченной\s+ответственностью)\b/g, '')
                              .replace(/[^a-zа-я0-9]+/g, ' ')
                              .replace(/\s+/g, ' ')
                              .trim();
                          };
                          const similarity = (a: string, b: string): number => {
                            const ta = normalize(a).split(/\s+/).filter(Boolean);
                            const tb = normalize(b).split(/\s+/).filter(Boolean);
                            if (ta.length === 0 || tb.length === 0) return 0;
                            const setA = new Set(ta);
                            const setB = new Set(tb);
                            let inter = 0;
                            setA.forEach(t => { if (setB.has(t)) inter += 1; });
                            const union = new Set([...ta, ...tb]).size;
                            return inter / union; // 0..1
                          };
                          const softPick = <T extends { id: number; name: string }>(items: T[], targetName?: string): T | undefined => {
                            if (!targetName || !items || items.length === 0) return undefined;
                            let best: { item: T; score: number } | undefined;
                            items.forEach(item => {
                              const score = similarity(item.name, targetName);
                              if (!best || score > best.score) best = { item, score };
                            });
                            return best && best.score >= 0.4 ? best.item : undefined;
                          };
                          if (!isLoadingReferences) {
                            if (d.clientName && clients && clients.length) {
                              const match = clients.find(c => normalize(c.name) === normalize(d.clientName)) || softPick(clients, d.clientName);
                              if (match) {
                                setSelectedClientId(match.id);
                                form.setFieldValue('clientId', String(match.id));
                                if (normalize(match.name) !== normalize(d.clientName)) {
                                  message.info(`Клиент подобран по имени: ${match.name}`);
                                }
                              }
                            }
                            if (d.expeditorName && expeditors && expeditors.length) {
                              const match = expeditors.find(x => normalize(x.name) === normalize(d.expeditorName)) || softPick(expeditors, d.expeditorName);
                              if (match) {
                                setSelectedExpeditorId(match.id);
                                form.setFieldValue('expeditorId', String(match.id));
                                if (normalize(match.name) !== normalize(d.expeditorName)) {
                                  message.info(`Экспедитор подобран по имени: ${match.name}`);
                                }
                              }
                            }
                            if (d.shipperName && String(d.shipperName).trim() !== '') {
                              console.log('🔍 Поиск грузоотправителя:', { 
                                shipperName: d.shipperName, 
                                shippersCount: shippers?.length,
                                isLoadingReferences 
                              });
                              if (shippers && shippers.length) {
                                const match = shippers.find(x => normalize(x.name) === normalize(d.shipperName)) || softPick(shippers, d.shipperName);
                                if (match) {
                                  console.log('✅ Грузоотправитель найден:', match.name);
                                  setSelectedShipperId(match.id);
                                  form.setFieldValue('shipperId', String(match.id));
                                  if (normalize(match.name) !== normalize(d.shipperName)) {
                                    message.info(`Грузоотправитель подобран по имени: ${match.name}`);
                                  }
                                } else {
                                  console.warn('⚠️ Грузоотправитель не найден в справочнике:', d.shipperName);
                                }
                              } else {
                                console.warn('⚠️ Справочник грузоотправителей пуст или не загружен');
                              }
                            } else {
                              console.log('ℹ️ Грузоотправитель не указан в OCR данных');
                            }
                            if (d.consigneeName && String(d.consigneeName).trim() !== '') {
                              console.log('🔍 Поиск грузополучателя:', { 
                                consigneeName: d.consigneeName, 
                                consigneesCount: consignees?.length,
                                isLoadingReferences 
                              });
                              if (consignees && consignees.length) {
                                const match = consignees.find(x => normalize(x.name) === normalize(d.consigneeName)) || softPick(consignees, d.consigneeName);
                                if (match) {
                                  console.log('✅ Грузополучатель найден:', match.name);
                                  setSelectedConsigneeId(match.id);
                                  form.setFieldValue('consigneeId', String(match.id));
                                  if (normalize(match.name) !== normalize(d.consigneeName)) {
                                    message.info(`Грузополучатель подобран по имени: ${match.name}`);
                                  }
                                } else {
                                  console.warn('⚠️ Грузополучатель не найден в справочнике:', d.consigneeName);
                                }
                              } else {
                                console.warn('⚠️ Справочник грузополучателей пуст или не загружен');
                              }
                            } else {
                              console.log('ℹ️ Грузополучатель не указан в OCR данных');
                            }
                          } else {
                            setPendingNames({
                              clientName: d.clientName,
                              expeditorName: d.expeditorName,
                              shipperName: d.shipperName,
                              consigneeName: d.consigneeName
                            });
                          }
                        } catch {}

                        // Fallback: если не распознали номер/дату — попробуем выдернуть их из rawText (независимо от других полей)
                        try {
                          const text: string = (json?.rawText || '').toString();
                          if (text) {
                            // Номер заявки: ищем общий шаблон, даже если есть адреса/стоимость распознаны
                            // Используем basisNumber вместо externalNumber
                            if (!d.externalNumber && !form.getFieldValue('basisNumber')) {
                              const numRe = /Заявка[^\n]*?№\s*([A-Za-zА-Яа-я0-9_./-]+)|\b(№|No\.?|N\.?|Номер)\s*[:#-]?\s*([A-Za-zА-Яа-я0-9_./-]+)/i;
                              const tlRe = /([A-Za-z]{1,4}[-_]?\d{2,4}[-_/.]?\d{1,5})/;
                              const m = text.match(numRe);
                              const rawExt = m ? (m[1] || m[3]) : undefined;
                              const rawOrTl = rawExt || (text.match(tlRe)?.[1]);
                              const cleanedExt = rawOrTl ? rawOrTl.trim().replace(/[\s\u00A0]+/g, '') : undefined;
                              const extNum = cleanedExt && /\d/.test(cleanedExt) && cleanedExt.length >= 3 ? cleanedExt : undefined;
                              if (extNum) {
                                form.setFieldValue('basisNumber', extNum);
                              }
                            }
                            // Дата заявки - используем basisDate вместо applicationDate
                            if (!d.applicationDate && !form.getFieldValue('basisDate')) {
                              // Ищем сначала после "от", затем любую дату
                              const afterOt = text.match(/Заявка[^\n]*?от\s*[«"]?(\d{1,2}[./-]\d{1,2}[./-]\d{2,4})/i)?.[1];
                              const anyDate = afterOt || text.match(/(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/)?.[0];
                              if (anyDate) {
                                const m = anyDate.match(/(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/);
                                if (m) {
                                  const dateStr = `${m[1].padStart(2,'0')}.${m[2].padStart(2,'0')}.${m[3].length===2?('20'+m[3]):m[3]}`;
                                  const dayjsDate = dayjs(dateStr, ['DD.MM.YYYY']);
                                  if (dayjsDate && typeof dayjsDate.isValid === 'function' && dayjsDate.isValid()) {
                                    form.setFieldValue('basisDate', dayjsDate);
                                  }
                                }
                              }
                            }
                          }
                        } catch {}

                        // Диагностика: что реально в форме после подстановки
                        setTimeout(() => {
                          const v = form.getFieldsValue();
                          console.log('✅ Form values after OCR import setFieldsValue:', {
                            basisNumber: v.basisNumber,
                            basisDate: v.basisDate?.format?.('DD.MM.YYYY'),
                            loadingAddress: v.loadingAddress,
                            unloadingAddress: v.unloadingAddress,
                            loadingContactPerson: v.loadingContactPerson,
                            unloadingContactPerson: v.unloadingContactPerson,
                            totalCostWithVAT: v.totalCostWithVAT,
                            requiredConditions: v.requiredConditions,
                            clientId: v.clientId,
                            expeditorId: v.expeditorId,
                            shipperId: v.shipperId,
                            consigneeId: v.consigneeId
                          });
                          // Если совсем ничего не распознано — положим rawText в "Необходимые условия"
                          // Проверяем basisNumber и basisDate вместо externalNumber и applicationDate
                          const hasRecognizedData = v.basisNumber || v.basisDate || v.loadingAddress || v.unloadingAddress || v.clientId || v.totalCostWithVAT;
                          if (!hasRecognizedData && json?.rawText) {
                            const currentConditions = form.getFieldValue('requiredConditions') || '';
                            const newConditions = currentConditions ? `${currentConditions}\n\n${json.rawText}` : json.rawText;
                            form.setFieldValue('requiredConditions', newConditions.trim());
                            message.info('Не удалось точно распознать поля. Поместил распознанный текст в раздел "Необходимые условия".');
                          }
                          // Сохраним в черновик, чтобы не потерялось при перерендерах
                          saveDraft();
                        }, 0);

                        // Краткая сводка пользователю, что подставлено
                        const applied: string[] = [];
                        if (d.externalNumber) applied.push('номер заявки');
                        if (d.applicationDate) applied.push('дата');
                        if (d.loadingAddress) applied.push('адрес погрузки');
                        if (d.unloadingAddress) applied.push('адрес выгрузки');
                        if (d.plannedLoadingDate) applied.push('дата погрузки');
                        if (d.plannedUnloadingDate) applied.push('дата выгрузки');
                        if (d.totalCostWithVAT != null) applied.push('стоимость');
                        if (d.requiredConditions) applied.push('условия');
                        if (d.clientName) applied.push('клиент (по имени)');
                        if (d.expeditorName) applied.push('экспедитор (по имени)');
                        if (d.shipperName) applied.push('отправитель (по имени)');
                        if (d.consigneeName) applied.push('получатель (по имени)');
                        if (applied.length) {
                          message.success(`Подставлены поля: ${applied.join(', ')}`);
                        }

                        // Если нашли груз — добавим в список (как локальный черновик)
                        if (Array.isArray(d.cargo) && d.cargo.length > 0) {
                          const c = d.cargo[0];
                          const newItem: CargoItem = {
                            key: `ocr_${Date.now()}`,
                            name: c.name || 'Груз',
                            description: '',
                            quantity: c.quantity || 1,
                            weightPerItem: c.weightPerItem || 0,
                            lengthM: c.lengthM ?? null,
                            widthM: c.widthM ?? null,
                            heightM: c.heightM ?? null,
                            totalWeight: (c.quantity || 1) * (c.weightPerItem || 0),
                            totalVolume: c.lengthM && c.widthM && c.heightM ? (c.lengthM * c.widthM * c.heightM * (c.quantity || 1)) : null,
                            packagingType: '',
                            cargoCharacter: 'обычный',
                            isLocal: true
                          };
                          setCargoList(prev => [newItem, ...prev]);
                        }

                        message.success('Данные из файла распознаны и подставлены');
                      } catch (err: any) {
                        console.error('Import file error', err);
                        message.error('Не удалось распознать файл');
                      } finally {
                        if (inputEl) inputEl.value = '';
                      }
                    }}
                  />
                  <Button icon={<UploadOutlined />} onClick={() => (document.getElementById('app-file-import') as HTMLInputElement)?.click()}>Импорт из файла</Button>
                </Space>
              </Col>
            </Row>

            <Divider orientation="left">Участники перевозки</Divider>

            {/* Основание заявки */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Divider orientation="left">Основание заявки</Divider>
                <Form.Item
                  label="Основание"
                  name="applicationBasis"
                  initialValue="CLIENT_CONTRACT"
                >
                  <Radio.Group>
                    <Radio value="CLIENT_CONTRACT">По договору</Radio>
                    <Radio value="CLIENT_CONTRACT_REQUEST">Договор-заявка клиента</Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  label="Клиент"
                  name="clientId"
                  rules={[{ required: true, message: 'Выберите клиента' }]}
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <SmartSelect
                      placeholder="Выберите клиента"
                      value={selectedClientId ? String(selectedClientId) : undefined}
                      onChange={(value: string) => {
                        console.log('✅ Client selected:', value);
                        if (value == null || value === '') {
                          setSelectedClientId(null);
                          form.setFieldValue('clientId', undefined);
                          form.setFieldValue('contractId', undefined);
                          return;
                        }
                        setSelectedClientId(Number(value));
                        form.setFieldValue('clientId', value);
                        form.setFieldValue('contractId', undefined);

                        if (clients) {
                          const selectedClient = clients.find(client => client.id === Number(value));
                          if (selectedClient) {
                            form.setFieldsValue({
                              loadingContactPerson: selectedClient.contactPerson || '',
                              loadingPhone: selectedClient.phone || ''
                            });
                          }
                        }
                      }}
                      options={(clients || []).map(client => ({ value: String(client.id), label: client.name }))}
                      loading={isLoadingClients}
                      disabled={isLoadingReferences}
                      allowClear
                    />
                    <Tooltip title="Добавить нового клиента">
                      <Button
                        icon={<PlusOutlined />}
                        disabled={isLoadingReferences}
                        onClick={() => openContractorModal('client', 'Добавить нового клиента')}
                      />
                    </Tooltip>
                  </Space.Compact>
                </Form.Item>

                {basisForContractUi === 'CLIENT_CONTRACT' && (
                  <>
                    {!clientIdForContracts && (
                      <Form.Item label="Договор" name="contractId">
                        <Select
                          disabled
                          placeholder="Сначала выберите клиента"
                          options={[]}
                        />
                      </Form.Item>
                    )}
                    {clientIdForContracts && contractsLoading && (
                      <Form.Item label="Договор">
                        <Text type="secondary">Загрузка…</Text>
                      </Form.Item>
                    )}
                    {clientIdForContracts && !contractsLoading && contractsByClient.length === 0 && (
                      <Form.Item label="Договор">
                        <Text type="secondary">У клиента нет договора</Text>
                      </Form.Item>
                    )}
                    {clientIdForContracts && !contractsLoading && contractsByClient.length === 1 && (
                      <Form.Item label="Договор">
                        <Space direction="vertical" size={4}>
                          <Text>
                            Договор №{contractsByClient[0].number} от{' '}
                            {dayjs(contractsByClient[0].date).format('DD.MM.YYYY')}
                          </Text>
                          <Form.Item name="contractId" noStyle>
                            <Input type="hidden" />
                          </Form.Item>
                        </Space>
                      </Form.Item>
                    )}
                    {clientIdForContracts && !contractsLoading && contractsByClient.length > 1 && (
                      <Form.Item label="Договор" name="contractId">
                        <Select
                          allowClear
                          placeholder="Выберите договор"
                          optionFilterProp="label"
                          showSearch
                          options={contractsByClient.map((c) => ({
                            value: c.id,
                            label: `Договор №${c.number} от ${dayjs(c.date).format('DD.MM.YYYY')}`
                          }))}
                          onChange={(value) => {
                            if (value == null) return;
                            const c = contractsByClient.find((x) => x.id === value);
                            if (!c) return;
                            applyFinancialFieldsFromContract(c);
                          }}
                        />
                      </Form.Item>
                    )}
                  </>
                )}

                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Номер"
                      name="basisNumber"
                      rules={[
                        { required: true, message: 'Укажите номер входящей заявки' }
                      ]}
                    >
                      <Input placeholder="Введите номер" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Дата"
                      name="basisDate"
                      normalize={(value: any) => {
                        if (!value) return undefined;
                        if (typeof value === 'string') {
                          const dayjsValue = dayjs(value);
                          return dayjsValue && typeof dayjsValue.isValid === 'function' && dayjsValue.isValid() ? dayjsValue : undefined;
                        }
                        if (value && typeof value.isValid === 'function') {
                          return value.isValid() ? value : undefined;
                        }
                        return undefined;
                      }}
                      rules={[
                        { required: true, message: 'Укажите дату входящей заявки' },
                        {
                          validator: (_: any, value: any) => {
                            if (value && typeof value.isValid === 'function' && !value.isValid()) {
                              return Promise.reject(new Error('Указана неверная дата'));
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        format="DD.MM.YYYY"
                        placeholder="Выберите дату"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Divider orientation="left">Контрагенты</Divider>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Экспедитор"
                  name="expeditorId"
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <SmartSelect
                      placeholder="Выберите экспедитора"
                      value={selectedExpeditorId ? String(selectedExpeditorId) : undefined}
                      onChange={(value: string) => {
                        console.log('✅ Expeditor selected:', value);
                        setSelectedExpeditorId(Number(value));
                        form.setFieldValue('expeditorId', value);
                      }}
                      options={(expeditors || []).map(expeditor => ({ value: String(expeditor.id), label: expeditor.name }))}
                      loading={isLoadingExpeditors}
                      disabled={isLoadingReferences}
                      allowClear
                    />
                    <Tooltip title="Добавить нового экспедитора">
                      <Button 
                        icon={<PlusOutlined />} 
                        disabled={isLoadingReferences}
                        onClick={() => openContractorModal('expeditor', 'Добавить нового экспедитора')}
                      />
                    </Tooltip>
                  </Space.Compact>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Грузоотправитель"
                  name="shipperId"
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <SmartSelect
                      placeholder="Выберите отправителя"
                      value={selectedShipperId ? String(selectedShipperId) : undefined}
                      onChange={(value: string) => {
                        console.log('✅ Shipper selected:', value);
                        setSelectedShipperId(Number(value));
                        form.setFieldValue('shipperId', value);

                        // Автозаполнение контактных данных (без адреса, чтобы не перезаписывать данные из импорта)
                        if (value && shippers) {
                          const selectedShipper = shippers.find(shipper => shipper.id === Number(value));
                          if (selectedShipper) {
                            form.setFieldsValue({
                              loadingContactPerson: selectedShipper.contactPerson || '',
                              loadingPhone: selectedShipper.phone || ''
                              // loadingAddress не автозаполняем, чтобы не перезаписывать данные из импорта
                            });
                          }
                        }
                      }}
                      options={(shippers || []).map(shipper => ({ value: String(shipper.id), label: shipper.name }))}
                      loading={isLoadingShippers}
                      disabled={isLoadingReferences}
                      allowClear
                    />
                    <Tooltip title="Добавить нового отправителя">
                      <Button
                        icon={<PlusOutlined />}
                        disabled={isLoadingReferences}
                        onClick={() => openContractorModal('shipper', 'Добавить нового грузоотправителя')}
                      />
                    </Tooltip>
                  </Space.Compact>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Грузополучатель"
                  name="consigneeId"
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <SmartSelect
                      placeholder="Выберите получателя"
                      value={selectedConsigneeId ? String(selectedConsigneeId) : undefined}
                      onChange={(value: string) => {
                        console.log('✅ Consignee selected:', value);
                        setSelectedConsigneeId(Number(value));
                        form.setFieldValue('consigneeId', value);
                        
                        // Автозаполнение контактных данных (без адреса, чтобы не перезаписывать данные из импорта)
                        if (value && consignees) {
                          const selectedConsignee = consignees.find(consignee => consignee.id === Number(value));
                          if (selectedConsignee) {
                            form.setFieldsValue({
                              unloadingContactPerson: selectedConsignee.contactPerson || '',
                              unloadingPhone: selectedConsignee.phone || ''
                              // unloadingAddress не автозаполняем, чтобы не перезаписывать данные из импорта
                            });
                          }
                        }
                      }}
                      options={(consignees || []).map(consignee => ({ value: String(consignee.id), label: consignee.name }))}
                      loading={isLoadingConsignees}
                      disabled={isLoadingReferences}
                      allowClear
                    />
                    <Tooltip title="Добавить нового получателя">
                      <Button 
                        icon={<PlusOutlined />} 
                        disabled={isLoadingReferences}
                        onClick={() => openContractorModal('consignee', 'Добавить нового грузополучателя')}
                      />
                    </Tooltip>
                  </Space.Compact>
                </Form.Item>
              </Col>
            </Row>

            {/* Ответственный логист */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Ответственный логист"
                  name="responsibleLogistId"
                >
                  <Select
                    placeholder="Выберите логиста"
                    showSearch
                    optionFilterProp="label"
                    disabled={!canEditResponsibleLogist || isLoadingUsers}
                    loading={isLoadingUsers}
                    allowClear
                    onChange={(value: number | null) => {
                      console.log('✅ Responsible logist changed:', value);
                      form.setFieldValue('responsibleLogistId', value);
                      setStep1Data((prev: any) => ({
                        ...prev,
                        responsibleLogistId: value !== null ? Number(value) : undefined
                      }));
                    }}
                  >
                    {mergeResponsibleSelectUsers(users, application?.responsibleLogist).map((user: any) => {
                      const label = formatResponsibleUserLabel(user);
                      return (
                        <Option key={user.id} value={user.id} label={label}>
                          {label}
                        </Option>
                      );
                    })}
                  </Select>
                  {!canEditResponsibleLogist && application?.responsibleLogist && (
                    <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                      {formatResponsibleUserLabel(application.responsibleLogist)}
                    </Text>
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Адреса погрузки и выгрузки</Divider>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Адрес погрузки"
                  name="loadingAddress"
                  rules={[{ required: true, message: 'Укажите адрес погрузки' }]}
                  tooltip="Точный адрес места погрузки груза"
                >
                  <AddressAutocomplete
                    placeholder="Введите адрес места погрузки..."
                    onChange={async (value, suggestion) => {
                      const v = typeof value === 'string' ? value : '';
                      if (!v.trim()) {
                        form.setFieldsValue({ loadingCity: '' });
                        setStep1Data((prev: any) => ({ ...prev, loadingCity: undefined }));
                        return;
                      }
                      const city = await resolveCityForAddressSelection(v, suggestion);
                      form.setFieldsValue({ loadingCity: city ?? '' });
                      setStep1Data((prev: any) => ({ ...prev, loadingCity: city }));
                    }}
                  />
                </Form.Item>
                <Form.Item name="loadingCity" hidden>
                  <Input />
                </Form.Item>
                <Row gutter={8}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Контактное лицо"
                      name="loadingContactPerson"
                    >
                      <Input placeholder="ФИО контактного лица" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Телефон"
                      name="loadingPhone"
                    >
                      <Input placeholder="+7 (xxx) xxx-xx-xx" />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Адрес выгрузки"
                  name="unloadingAddress"
                  rules={[{ required: true, message: 'Укажите адрес выгрузки' }]}
                  tooltip="Точный адрес места выгрузки груза"
                >
                  <AddressAutocomplete
                    placeholder="Введите адрес места выгрузки..."
                    onChange={async (value, suggestion) => {
                      const v = typeof value === 'string' ? value : '';
                      if (!v.trim()) {
                        form.setFieldsValue({ unloadingCity: '' });
                        setStep1Data((prev: any) => ({ ...prev, unloadingCity: undefined }));
                        return;
                      }
                      const city = await resolveCityForAddressSelection(v, suggestion);
                      form.setFieldsValue({ unloadingCity: city ?? '' });
                      setStep1Data((prev: any) => ({ ...prev, unloadingCity: city }));
                    }}
                  />
                </Form.Item>
                <Form.Item name="unloadingCity" hidden>
                  <Input />
                </Form.Item>
                <Row gutter={8}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Контактное лицо"
                      name="unloadingContactPerson"
                    >
                      <Input placeholder="ФИО контактного лица" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Телефон"
                      name="unloadingPhone"
                    >
                      <Input placeholder="+7 (xxx) xxx-xx-xx" />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Divider orientation="left">Плановые даты</Divider>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Планируемая дата погрузки"
                  name="plannedLoadingDate"
                  normalize={(value: any) => {
                        // Нормализуем значение: если это строка, конвертируем в dayjs
                        if (!value) return undefined;
                        if (typeof value === 'string') {
                          const dayjsValue = dayjs(value);
                          return dayjsValue && typeof dayjsValue.isValid === 'function' && dayjsValue.isValid() ? dayjsValue : undefined;
                        }
                        // Если это уже dayjs объект, возвращаем как есть
                        if (value && typeof value.isValid === 'function') {
                          return value.isValid() ? value : undefined;
                        }
                        return undefined;
                      }}
                  rules={[
                    { required: true, message: 'Укажите дату погрузки' },
                    {
                      validator: (_: any, value: any) => {
                        if (!value) return Promise.resolve();
                        // Проверяем, что значение валидно (может быть dayjs объектом, строкой или null)
                        if (value && typeof value.isValid === 'function' && !value.isValid()) {
                          return Promise.reject(new Error('Указана неверная дата погрузки'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD.MM.YYYY"
                    placeholder="Выберите дату погрузки"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Планируемая дата выгрузки"
                  name="plannedUnloadingDate"
                  normalize={(value: any) => {
                        // Нормализуем значение: если это строка, конвертируем в dayjs
                        if (!value) return undefined;
                        if (typeof value === 'string') {
                          const dayjsValue = dayjs(value);
                          return dayjsValue && typeof dayjsValue.isValid === 'function' && dayjsValue.isValid() ? dayjsValue : undefined;
                        }
                        // Если это уже dayjs объект, возвращаем как есть
                        if (value && typeof value.isValid === 'function') {
                          return value.isValid() ? value : undefined;
                        }
                        return undefined;
                      }}
                  rules={[
                    { required: true, message: 'Укажите дату выгрузки' },
                    {
                      validator: (_: any, value: any) => {
                        if (!value) return Promise.resolve();
                        // Проверяем, что значение валидно (может быть dayjs объектом, строкой или null)
                        if (value && typeof value.isValid === 'function' && !value.isValid()) {
                          return Promise.reject(new Error('Указана неверная дата выгрузки'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD.MM.YYYY"
                    placeholder="Выберите дату выгрузки"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Дополнительная информация</Divider>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Ставка услуг по заявке с НДС"
                  name="totalCostWithVAT"
                  tooltip="Общая стоимость всех услуг по заявке включая НДС"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                    addonAfter="₽"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Способ оплаты от клиента"
                  name="paymentMethod"
                  rules={[{ required: true, message: 'Выберите способ оплаты' }]}
                >
                  <Select placeholder="Выберите способ оплаты">
                    <Option value="cash">Наличные</Option>
                    <Option value="non_cash_vat">Безналичный с НДС</Option>
                    <Option value="non_cash_no_vat">Безналичный без НДС</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Финансовые условия */}
            <>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Тип предоплаты"
                      name="prepaymentCondition"
                    >
                      <Select placeholder="Выберите тип предоплаты">
                        <Option value="BEFORE_LOADING">До погрузки</Option>
                        <Option value="AFTER_LOADING">После погрузки</Option>
                        <Option value="BEFORE_UNLOADING">До выгрузки</Option>
                        <Option value="AFTER_UNLOADING">После выгрузки</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={6}>
                    <Form.Item
                      label="Предоплата (%)"
                      name="prepaymentPercent"
                      rules={[{ type: 'number', min: 0, max: 100, message: 'От 0 до 100' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="0"
                        min={0}
                        max={100}
                        onChange={(value) => {
                          if (value !== null && value !== undefined) {
                            form.setFieldsValue({ mainPaymentPercent: 100 - value });
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item
                      label="Доплата (%)"
                      name="mainPaymentPercent"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="100"
                        min={0}
                        max={100}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item
                      label="Отсрочка предоплаты (дней)"
                      name="prepaymentDelayDays"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="0"
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item
                      label="Отсрочка доплаты (дней)"
                      name="mainPaymentDelayDays"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="0"
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  ℹ️ Счётчик предоплаты запускается вручную из финансового модуля. 
                  Счётчик доплаты запустится автоматически при завершении всех маршрутов по заявке.
                </Text>
            </>

            <Card
              size="small"
              title="Условия и штрафы"
              extra={
                <Button size="small" icon={<PlusOutlined />} onClick={() => openConditionModal()}>
                  Добавить условие
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              <DataTable
                rowKey={(_, index) => String(index)}
                size="small"
                pagination={false}
                dataSource={conditions}
                locale={{ emptyText: 'Условия и штрафы не добавлены' }}
                columns={[
                  {
                    title: 'Тип',
                    dataIndex: 'type',
                    key: 'type',
                    render: (type: ApplicationConditionType) => APPLICATION_CONDITION_LABELS[type] || type,
                  },
                  {
                    title: 'Условия',
                    key: 'terms',
                    render: (_: unknown, record: ApplicationCondition) => formatApplicationCondition(record).terms,
                  },
                  {
                    title: 'Размер',
                    key: 'amount',
                    render: (_: unknown, record: ApplicationCondition) => formatApplicationCondition(record).amount,
                  },
                  {
                    title: 'Комментарий',
                    dataIndex: 'comment',
                    key: 'comment',
                    render: (comment?: string | null) => comment || '—',
                  },
                  {
                    title: 'Действия',
                    key: 'actions',
                    width: 140,
                    render: (_: unknown, record: ApplicationCondition, index: number) => (
                      <Space>
                        <Button size="small" icon={<EditOutlined />} onClick={() => openConditionModal(record, index)} />
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteCondition(index)} />
                      </Space>
                    ),
                  },
                ]}
              />
            </Card>

            <Form.Item
              label="Необходимые условия"
              name="requiredConditions"
              tooltip="Особые требования к перевозке, дополнительные условия"
            >
              <TextArea
                rows={3}
                placeholder="Укажите особые требования к перевозке, температурный режим, способы погрузки/разгрузки и другие необходимые условия..."
                maxLength={1000}
                showCount
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Space>
                <Button
                  type="primary"
                  onClick={handleStep1Submit}
                  size="large"
                >
                  Далее: Добавить грузы
                </Button>
                {!isEdit && (
                  <Button onClick={() => { clearDraft(); form.resetFields(); setCargoList([]); setCurrentStep(0); }} size="large">
                    Очистить форму
                  </Button>
                )}
                <Button
                  onClick={handleCancel}
                  size="large"
                >
                  Отмена
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}

        {currentStep === 1 && (
          <div>
            <Row justify="space-between" align="middle" className="mb-4">
              <Col>
                <Title level={4} className="mb-0">
                  Добавление грузов
                </Title>
                <Text type="secondary">
                  Всего грузов: {cargoList.length}
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => setCargoImportModalVisible(true)}
                  >
                    Импорт из Excel
                  </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddCargo}
                >
                  Добавить груз
                </Button>
                </Space>
              </Col>
            </Row>

            {/* Сводка по грузу */}
            {cargoList.length > 0 && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                  <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f0f8ff' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
                      {cargoList.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Общее количество мест</div>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f6ffed' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}>
                      {(() => {
                        const totalWeight = cargoList.reduce((sum, item) => sum + (item.totalWeight || 0), 0);
                        return totalWeight && !isNaN(totalWeight) ? totalWeight.toLocaleString('ru-RU') : '0';
                      })()}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Общий вес (кг)</div>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card size="small" style={{ textAlign: 'center', backgroundColor: '#fff7e6' }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fa8c16' }}>
                      {(() => {
                        const totalVolume = cargoList.reduce((sum, item) => sum + (item.totalVolume || 0), 0);
                        return totalVolume && !isNaN(totalVolume) ? totalVolume.toFixed(2) : '0.00';
                      })()}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Общий объем (м³)</div>
                  </Card>
                </Col>
              </Row>
            )}

            <DataTable
              columns={cargoColumns}
              dataSource={cargoList}
              pagination={false}
              size="middle"
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'Нет добавленных грузов' }}
            />
            
            {/* Итоговая строка */}
            {cargoList.length > 0 && (
              <Row 
                style={{ 
                  marginTop: 16, 
                  marginBottom: 16,
                  padding: '12px 16px', 
                  backgroundColor: '#fafafa', 
                  borderRadius: '6px',
                  border: '1px solid #f0f0f0'
                }}
              >
                <Col span={6}>
                  <Text strong>ИТОГО:</Text>
                </Col>
                <Col span={6} style={{ textAlign: 'center' }}>
                  <Text strong style={{ color: '#1890ff' }}>
                    {cargoList.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0} мест
                  </Text>
                </Col>
                <Col span={6} style={{ textAlign: 'center' }}>
                  <Text strong style={{ color: '#52c41a' }}>
                    {(() => {
                      const totalWeight = cargoList.reduce((sum, item) => sum + (item.totalWeight || 0), 0);
                      return totalWeight && !isNaN(totalWeight) ? totalWeight.toLocaleString('ru-RU') : '0';
                    })()} кг
                  </Text>
                </Col>
                <Col span={6} style={{ textAlign: 'center' }}>
                  <Text strong style={{ color: '#fa8c16' }}>
                    {(() => {
                      const totalVolume = cargoList.reduce((sum, item) => sum + (item.totalVolume || 0), 0);
                      return totalVolume && !isNaN(totalVolume) ? totalVolume.toFixed(2) : '0.00';
                    })()} м³
                  </Text>
                </Col>
              </Row>
            )}

            <Divider />

            <Space>
              <Button
                onClick={handleBackToStep1}
                size="large"
              >
                Назад
              </Button>
              <Button
                type="primary"
                onClick={handleFinalSubmit}
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                disabled={cargoList.length === 0}
              >
                {isEdit ? 'Сохранить изменения' : 'Создать заявку'}
              </Button>
              {!isEdit && (
                <Button onClick={() => { clearDraft(); form.resetFields(); setCargoList([]); setCurrentStep(0); }} size="large">
                  Очистить форму
                </Button>
              )}
              <Button
                onClick={handleCancel}
                disabled={loading}
                size="large"
              >
                Отмена
              </Button>
            </Space>
          </div>
        )}

        {/* Модальное окно добавления/редактирования груза */}
        <Modal
          title={editingCargo ? 'Редактирование груза' : 'Добавление груза'}
          open={cargoModalVisible}
          onOk={handleCargoSubmit}
          onCancel={() => setCargoModalVisible(false)}
          width={800}
          okText={editingCargo ? 'Сохранить' : 'Добавить'}
          cancelText="Отмена"
        >
          <Form
            form={cargoForm}
            layout="vertical"
            onValuesChange={(changedValues, allValues) => {
              const quantity = allValues.quantity || 1;
              
              // Пересчитываем вес при изменении количества или веса за единицу
              // НЕ затираем, если пользователь ввёл общий вес вручную
              if (changedValues.quantity || changedValues.weightPerItem) {
                const weightPerItem = allValues.weightPerItem;
                // Рассчитываем вес только если указан вес места
                if (weightPerItem && weightPerItem > 0) {
                  const calculatedWeight = quantity * weightPerItem;
                  cargoForm.setFieldsValue({ totalWeight: calculatedWeight });
                }
              }
              
              // Пересчитываем объём при изменении габаритов или количества
              // НЕ затираем, если пользователь ввёл объём вручную
              if (changedValues.lengthM || changedValues.widthM || changedValues.heightM || changedValues.quantity) {
                const lengthM = allValues.lengthM;
                const widthM = allValues.widthM;
                const heightM = allValues.heightM;
                
                // Рассчитываем объём только если ВСЕ габариты указаны
                if (lengthM && widthM && heightM) {
                  const calculatedVolume = quantity * lengthM * widthM * heightM;
                  cargoForm.setFieldsValue({ totalVolume: calculatedVolume });
                }
              }
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Наименование груза"
                  name="name"
                  rules={[{ required: true, message: 'Введите наименование груза' }]}
                >
                  <Input placeholder="Например: Электроника" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Характер груза"
                  name="cargoCharacter"
                  rules={[{ required: true, message: 'Выберите характер груза' }]}
                >
                  <Select placeholder="Выберите характер груза">
                    <Option value="обычный">Обычный</Option>
                    <Option value="негабаритный">Негабаритный</Option>
                    <Option value="опасный">Опасный</Option>
                    <Option value="хрупкий">Хрупкий</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Описание груза"
              name="description"
            >
              <TextArea rows={2} placeholder="Подробное описание груза..." />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Количество мест"
                  name="quantity"
                  rules={[{ required: true, message: 'Укажите количество' }]}
                >
                  <InputNumber min={1} placeholder="1" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Вес одного места (кг)"
                  name="weightPerItem"
                  tooltip="Необязательно, если указан общий вес"
                >
                  <InputNumber min={0} placeholder="100" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Вид упаковки"
                  name="packagingType"
                >
                  <Input placeholder="Коробки, паллеты..." />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Габариты места (метры)</Divider>

            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Form.Item
                  label="Длина (м)"
                  name="lengthM"
                  rules={[{ required: false, message: 'Укажите длину' }]}
                >
                  <InputNumber min={0} step={0.01} placeholder="1.2" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  label="Ширина (м)"
                  name="widthM"
                  rules={[{ required: false, message: 'Укажите ширину' }]}
                >
                  <InputNumber min={0} step={0.01} placeholder="0.8" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  label="Высота (м)"
                  name="heightM"
                  rules={[{ required: false, message: 'Укажите высоту' }]}
                >
                  <InputNumber min={0} step={0.01} placeholder="0.6" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Итого (авто или вручную)</Divider>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item 
                  label="Общий вес (кг)" 
                  name="totalWeight"
                  tooltip="Рассчитывается автоматически из веса места × количество. Если вес места не указан — введите вручную."
                >
                  <InputNumber 
                    min={0} 
                    step={1} 
                    style={{ width: '100%' }} 
                    placeholder="Авто или вручную"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item 
                  label="Общий объем (м³)" 
                  name="totalVolume"
                  tooltip="Рассчитывается автоматически из габаритов. Если габариты не указаны — введите вручную."
                >
                  <InputNumber 
                    min={0} 
                    step={0.01} 
                    style={{ width: '100%' }} 
                    placeholder="Авто или вручную"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        <Modal
          title={editingConditionIndex === null ? 'Добавить условие' : 'Редактировать условие'}
          open={conditionModalVisible}
          onOk={handleSaveCondition}
          onCancel={() => {
            setConditionModalVisible(false);
            conditionForm.resetFields();
          }}
          okText="Сохранить"
          cancelText="Отмена"
          destroyOnClose
        >
          <Form form={conditionForm} layout="vertical">
            <Form.Item
              name="type"
              label="Тип условия"
              rules={[{ required: true, message: 'Выберите тип условия' }]}
            >
              <Select
                options={APPLICATION_CONDITION_OPTIONS}
                placeholder="Выберите тип"
                onChange={() => {
                  conditionForm.setFieldsValue({
                    calculationType: undefined,
                    valueType: undefined,
                    value: undefined,
                    thresholdDays: undefined,
                    deadlineDays: undefined,
                  });
                }}
              />
            </Form.Item>

            {(selectedConditionType === 'LOADING_FAILURE' ||
              selectedConditionType === 'UNLOADING_FAILURE' ||
              selectedConditionType === 'VEHICLE_CHANGE') && (
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    name="calculationType"
                    label="Тип расчёта"
                    rules={[{ required: true, message: 'Укажите тип расчёта' }]}
                  >
                    <Select
                      options={[
                        { value: 'fixed', label: 'Фиксированная сумма' },
                        { value: 'percent', label: 'Процент' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="value" label="Размер" rules={[{ required: true, message: 'Укажите размер' }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {(selectedConditionType === 'LATE_LOADING' || selectedConditionType === 'LATE_UNLOADING') && (
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="thresholdDays" label="Порог, дней" rules={[{ required: true, message: 'Укажите порог' }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="valueType" label="Тип значения" rules={[{ required: true, message: 'Укажите тип' }]}>
                    <Select
                      options={[
                        { value: 'fixed', label: 'Сумма' },
                        { value: 'percent', label: 'Процент' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="value" label="Размер / день" rules={[{ required: true, message: 'Укажите размер' }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {selectedConditionType === 'DOCUMENTS' && (
              <>
                <Form.Item
                  name="deadlineDays"
                  label="Срок предоставления оригиналов, дней"
                  rules={[{ required: true, message: 'Укажите срок' }]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Штраф можно не указывать, если в договоре задан только срок.
                </Text>
                <Row gutter={12}>
                  <Col span={8}>
                    <Form.Item name="calculationType" label="Тип штрафа">
                      <Select
                        allowClear
                        options={[
                          { value: 'fixed', label: 'Фикс.' },
                          { value: 'percent', label: 'Процент' },
                          { value: 'per_day', label: 'За день' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="valueType" label="Тип значения">
                      <Select
                        allowClear
                        options={[
                          { value: 'fixed', label: 'Сумма' },
                          { value: 'percent', label: 'Процент' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="value" label="Размер">
                      <InputNumber style={{ width: '100%' }} min={0} />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            {selectedConditionType === 'NO_LOCATION' && (
              <Form.Item name="value" label="Сумма за день" rules={[{ required: true, message: 'Укажите сумму' }]}>
                <InputNumber style={{ width: '100%' }} min={0} addonAfter="₽ / день" />
              </Form.Item>
            )}

            <Form.Item name="comment" label="Комментарий">
              <TextArea rows={3} maxLength={500} showCount placeholder="Опционально" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Единое модальное окно создания контрагента */}
        <CreateContractorModal
          isOpen={contractorModalVisible}
          onCancel={() => setContractorModalVisible(false)}
          onSuccess={handleContractorCreated}
          defaultRoles={contractorDefaultRoles}
        />

        {/* Модальное окно импорта грузов */}
        <CargoImportModal
          visible={cargoImportModalVisible}
          onCancel={() => setCargoImportModalVisible(false)}
          onSuccess={handleImportCargo}
        />

    </div>
    </Modal>
  );
};

export default ApplicationForm; 